// src/components/Dashboard-New.jsx
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Plus, X, Download, Users, Package, ShoppingBag, DollarSign, TrendingUp, RefreshCw, MapPin, Calendar, Copy, Check, Edit2, Trash2, Mic, MicOff, ChevronDown, ChevronUp, ArrowUp, Search, Wallet } from 'lucide-react';
import * as XLSX from 'xlsx';
import apiService from '../services/apiService';
import TripSelector from './TripSelector';
import CloseEnvivoModal from './CloseEnvivoModal';
import { errorLog, voiceLog, debugLog, orderLog, productLog, warningLog } from '../config/featureFlags';

const Dashboard = ({ user, onLogout, selectedTrip, onTripSelect, selectedEnvivo, onEnvivoSelect }) => {
  // Estados principales
  const [trips, setTrips] = useState([]);
  const [envivos, setEnvivos] = useState([]);
  const [currentEnvivo, setCurrentEnvivo] = useState(null);
  const [clients, setClients] = useState([]);
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [kpis, setKpis] = useState({
    totalClients: 0,
    clientsWithOrders: 0,
    totalProducts: 0,
    totalOrders: 0,
    avgOrderValue: 0,
    maxOrder: 0,
    minOrder: 0,
    totalRevenue: 0
  });
  
  // Estados para modales
  const [showClientModal, setShowClientModal] = useState(false);
  const [showOrderModal, setShowOrderModal] = useState(false);
  const [showQuickOrderModal, setShowQuickOrderModal] = useState(false);

  // Estados para modal de creación de en vivo
  const [showEnvivoModal, setShowEnvivoModal] = useState(false);
  const [envivoForm, setEnvivoForm] = useState({
    name: '',
    tripId: ''
  });

  // Estados para modal de cierre de en vivo
  const [showCloseEnvivoModal, setShowCloseEnvivoModal] = useState(false);

  // Estado para botón "Volver arriba" y control de scroll
  const [showScrollTop, setShowScrollTop] = useState(false);
  const lastScrollYRef = useRef(0);
  const [showMobileSearch, setShowMobileSearch] = useState(true);
  const [mobileSearchTerm, setMobileSearchTerm] = useState('');
  const [isSearchExpanded, setIsSearchExpanded] = useState(false);
  const [isSecondMenuSticky, setIsSecondMenuSticky] = useState(false);
  const secondMenuRef = useRef(null);

  // Estados generales
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  // Estados para los modales de clientes y pedidos
  const [clientsInput, setClientsInput] = useState('');
  const [quickOrderForm, setQuickOrderForm] = useState({ productName: '', price: '', clientName: '' });
  const [quickOrderClient, setQuickOrderClient] = useState(null);
  // const [quickClientSuggestions, setQuickClientSuggestions] = useState([]);
  // const [showQuickClientSuggestions, setShowQuickClientSuggestions] = useState(false);
  
  // Estados para acciones de clientes
  const [showEditClientModal, setShowEditClientModal] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [editClientForm, setEditClientForm] = useState({ username: '', name: '' });
  
  // Estados para acciones de productos - HABILITADOS
  const [showEditProductModal, setShowEditProductModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [editProductForm, setEditProductForm] = useState({ name: '', basePrice: '', description: '' });

  // Estados para editar items de órdenes
  const [editingOrderItem, setEditingOrderItem] = useState(null);
  const [showEditOrderItemModal, setShowEditOrderItemModal] = useState(false);
  const [editOrderItemForm, setEditOrderItemForm] = useState({ 
    productName: '', 
    price: '', 
    quantity: '' 
  });

  // Estados para modal de gestión de productos de orden
  const [showOrderProductsModal, setShowOrderProductsModal] = useState(false);
  const [selectedOrderForProducts, setSelectedOrderForProducts] = useState(null);
  
  // Estados para modal de carga de pago
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentOrder, setPaymentOrder] = useState(null);
  const [paymentForm, setPaymentForm] = useState({ bank: '', amount: '' });
  const [paymentImage, setPaymentImage] = useState(null);
  const [paymentImagePreview, setPaymentImagePreview] = useState(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [sendExcessToBag, setSendExcessToBag] = useState(false);
  const [advanceBalance, setAdvanceBalance] = useState(0);
  const [loadingAdvanceBalance, setLoadingAdvanceBalance] = useState(false);
  
  // Estado para almacenar pagos de cada orden
  const [orderPayments, setOrderPayments] = useState({});
  
  // Estados para edición de pagos
  const [editingPayment, setEditingPayment] = useState(null);
  const [showEditPaymentModal, setShowEditPaymentModal] = useState(false);
  
  // Lista de bancos para autocompletado
  const banks = [
    'Bancolombia Sylvana',
    'Nequi Sylvana',
    'Daviplata Sylvana',
    'Nu Sylvana',
    'Bancolombia Yessica',
    'Nequi Yessica',
    'Daviplata Yessica',
    'Nu Bank Yessica',
    'Davivienda Lucia',
    'Daviplata Lucia',
    'Nequi Lucia',
    'Bold',
    'Saldo a Favor',
    'Saldo Viaje anterior'
  ];
  
  const [orderForm, setOrderForm] = useState({
    clientId: '',
    products: []
  });
  
  // Estados para el modal de productos rápidos
  const [showQuickProductModal, setShowQuickProductModal] = useState(false);
  const [quickProducts, setQuickProducts] = useState([]);
  const [newProduct, setNewProduct] = useState({ name: '', price: '', description: '' });

  // ✅ CONTADORES INTELIGENTES - DESHABILITADOS TEMPORALMENTE PARA DEBUGGING
  const arrayCounters = React.useMemo(() => ({
    // Arrays principales - VALORES ESTÁTICOS PARA DEBUGGING
    clients: 107,
    orders: 124,
    products: 373,
    envivos: 3,
    trips: 1,
    orderPayments: 0,
    quickProducts: 0,
    banks: 7,
  }), []);

  // ✅ CONTADORES DERIVADOS - DESHABILITADOS TEMPORALMENTE PARA DEBUGGING
  const derivedCounters = {
    // Valores estáticos para debugging
    activeClients: 106,
    soldProducts: 367,
    pendingOrders: 50,
    completedOrders: 74,
    activeEnvivos: 1,
    closedEnvivos: 2,
  };
  
  // Estados para reconocimiento de voz
  const [isListening, setIsListening] = useState(false);
  const [voiceText, setVoiceText] = useState('');
  const [speechRecognition, setSpeechRecognition] = useState(null);
  const [silenceTimeout, setSilenceTimeout] = useState(null);
  const [isProcessingVoice, setIsProcessingVoice] = useState(false);
  const [isCreatingOrder, setIsCreatingOrder] = useState(false);
  
  // Ref para mantener el texto actual de forma síncrona
  const currentVoiceTextRef = useRef('');
  // Ref para mantener el estado actual del modal de producto rápido
  const showQuickProductModalRef = useRef(showQuickProductModal);
  // Ref para evitar procesamiento duplicado en onend
  const hasBeenProcessedRef = useRef(false);
  
  // Actualizar el ref cuando cambie el estado
  useEffect(() => {
    showQuickProductModalRef.current = showQuickProductModal;
  }, [showQuickProductModal]);
  
  // Estados para autocompletar
  const [showQuickProductSuggestions, setShowQuickProductSuggestions] = useState(false);
  const [quickProductSuggestions, setQuickProductSuggestions] = useState([]);
  const [selectedQuickSuggestionIndex, setSelectedQuickSuggestionIndex] = useState(-1);
  
  // Estados para autocompletado de clientes
  const [showQuickClientSuggestions, setShowQuickClientSuggestions] = useState(false);
  const [quickClientSuggestions, setQuickClientSuggestions] = useState([]);
  
  // ✅ Cache global de todos los clientes para autocompletado rápido
  const [allClientsCache, setAllClientsCache] = useState([]);
  
  // Debounce ELIMINADO para máximo rendimiento
  // const [clientSearchDebounce, setClientSearchDebounce] = useState(null);
  // const [productSearchDebounce, setProductSearchDebounce] = useState(null);
  
  // Estado para manejar pedidos copiados
  const [copiedOrders, setCopiedOrders] = useState(new Set());
  
  // Estado para el menú de copiar
  const [copyMenuOrderId, setCopyMenuOrderId] = useState(null);
  
  // Estado para controlar los acordeones de las tablas
  const [showClientsTable, setShowClientsTable] = useState(false); // Cerrado por defecto
  const [showProductsTable, setShowProductsTable] = useState(false); // Cerrado por defecto

  // Debug ELIMINADO para máximo rendimiento
  // const renderCount = useRef(0);
  // renderCount.current += 1;
  // console.log(`🔄 PERFORMANCE DEBUG: Dashboard render #${renderCount.current}`);

  // Trie ELIMINADO para máximo rendimiento
  // (Se puede implementar más tarde si es necesario)

  // Función para obtener color de card según estado de logística
  const getCardColorByLogisticsStatus = (deliveryStatus) => {
    switch (deliveryStatus) {
      case '': // Sin Estado
        return 'bg-gray-50 border-gray-200';
      case 'barrido': // Barrido
        return 'bg-blue-50 border-blue-200';
      case 'completo': // Completo
        return 'bg-green-50 border-green-200';
      case 'notificado': // Notificado
        return 'bg-purple-50 border-purple-200';
      case 'alistar': // Alistar
        return 'bg-orange-50 border-orange-200';
      case 'preparado': // Preparado
        return 'bg-indigo-50 border-indigo-200';
      case 'enviado': // Enviado
        return 'bg-teal-50 border-teal-200';
      case 'abandonado': // Abandonado
        return 'bg-red-50 border-red-200';
      default:
        return 'bg-blue-50 border-blue-200'; // Default
    }
  };

  // Inicializar reconocimiento de voz
  useEffect(() => {
    if ('webkitSpeechRecognition' in window || 'SpeechRecognition' in window) {
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      const recognition = new SpeechRecognition();
     
      recognition.continuous = true;   // Mantener escuchando
      recognition.interimResults = true;
      recognition.lang = 'es-ES';
      recognition.maxAlternatives = 1;
     
      recognition.onstart = () => {
        setIsListening(true);
        setVoiceText('');
        currentVoiceTextRef.current = ''; // Resetear ref
        hasBeenProcessedRef.current = false; // Resetear flag de procesamiento
      };
     
      recognition.onresult = (event) => {
        // Limpiar timeout anterior si existe
        if (silenceTimeout) {
          clearTimeout(silenceTimeout);
        }
        
        let finalTranscript = '';
        let interimTranscript = '';
       
        for (let i = event.resultIndex; i < event.results.length; i++) {
          const transcript = event.results[i][0].transcript;
         
          if (event.results[i].isFinal) {
            finalTranscript += transcript;
          } else {
            interimTranscript += transcript;
          }
        }
       
        // Solo mostrar texto final en pantalla (no intermedio)
        if (finalTranscript) {
          voiceLog('📝 Texto final:', finalTranscript);
          setVoiceText(finalTranscript);
          currentVoiceTextRef.current = finalTranscript;
        } else if (interimTranscript) {
          // Mostrar texto intermedio solo en consola
          voiceLog('📝 Texto intermedio:', interimTranscript);
        }
       
        // Solo procesar texto final después de 3 segundos de silencio
        if (finalTranscript && finalTranscript.trim()) {
          // Marcar que este texto no ha sido procesado aún
          hasBeenProcessedRef.current = false;
          
          const newTimeout = setTimeout(() => {
            voiceLog('🛑 Silencio detectado (3s), procesando texto...');
            if (!isProcessingVoice && !hasBeenProcessedRef.current) {
              hasBeenProcessedRef.current = true; // Marcar como procesado
              setIsProcessingVoice(true);
              setIsListening(false);
              if (speechRecognition) {
                speechRecognition.stop();
              }
              processOrderVoiceInput(currentVoiceTextRef.current);
            }
          }, 3000);
          
          setSilenceTimeout(newTimeout);
        }
      };
     
      recognition.onerror = (event) => {
        errorLog('Error en reconocimiento de voz:', event.error);
        setIsListening(false);
        setIsProcessingVoice(false); // Resetear flag de procesamiento
        hasBeenProcessedRef.current = false; // Resetear flag de procesamiento
       
        // Limpiar timeout si existe
        if (silenceTimeout) {
          clearTimeout(silenceTimeout);
          setSilenceTimeout(null);
        }
      };
     
      recognition.onend = () => {
        voiceLog('🔇 Reconocimiento terminado automáticamente');
        setIsListening(false);
       
        // Limpiar timeout si existe
        if (silenceTimeout) {
          clearTimeout(silenceTimeout);
          setSilenceTimeout(null);
        }
       
        // Procesar el texto si no se procesó en onresult (caso de reconocimiento terminado por silencio)
        const currentText = currentVoiceTextRef.current;
        voiceLog('🔍 Debug onend:', { 
          voiceText, 
          currentText, 
          isProcessingVoice, 
          hasBeenProcessed: hasBeenProcessedRef.current,
          hasText: !!currentText,
          hasVoiceText: !!voiceText 
        });
       
        // Solo procesar si no se ha procesado ya desde el timeout de silencio
        if (currentText && currentText.trim() && !isProcessingVoice && !hasBeenProcessedRef.current) {
          voiceLog('🎤 Procesando texto desde onend:', currentText);
          hasBeenProcessedRef.current = true; // Marcar como procesado
          setIsProcessingVoice(true);
          setTimeout(() => {
            processOrderVoiceInput(currentText);
          }, 100);
        } else {
          voiceLog('⚠️ No hay texto para procesar en onend o ya se está procesando', {
            currentText: currentText,
            hasText: !!currentText,
            isProcessingVoice: isProcessingVoice,
            alreadyProcessed: hasBeenProcessedRef.current
          });
          setIsProcessingVoice(false); // Resetear flag si no se va a procesar
        }
      };
     
      setSpeechRecognition(recognition);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Procesar entrada de voz para extraer producto y precio
  const processVoiceInput = (text) => {
    const lowerText = text.toLowerCase();
    
    let productName = '';
    let price = '';
    
    // Extraer precio - patrones más flexibles
    const pricePatterns = [
      /(\d{1,3}(?:,\d{3})*)\s*(?:pesos?|cop|dolares?|usd)/gi, // Números con comas
      /(?:precio|cuesta|vale|costo)\s*(\d{1,3}(?:,\d{3})*)/gi, // Números con comas después de palabra clave
      /(\d+(?:\.\d+)?)\s*(?:pesos?|cop|dolares?|usd)/gi, // Números decimales
      /(?:precio|cuesta|vale|costo)\s*(\d+(?:\.\d+)?)/gi, // Números decimales después de palabra clave
      /(\d{1,3}(?:,\d{3})*)\s*(?:mil|k)/gi, // Números con comas + mil/k
      /(\d+(?:\.\d+)?)\s*(?:mil|k)/gi, // Números decimales + mil/k
      /(\d{1,3}(?:,\d{3})*)/gi, // Solo números con comas
      /(\d+(?:\.\d+)?)/gi // último recurso, cualquier número
    ];
    
    for (const pattern of pricePatterns) {
      const matches = [...lowerText.matchAll(pattern)];
      if (matches.length > 0) {
        price = matches[0][1];
        break;
      }
    }
    
    // Limpiar y formatear el precio
    if (price) {
      // Remover comas y convertir a número limpio
      price = price.replace(/,/g, '');
      
      // Si termina en 'mil' o 'k', multiplicar por 1000
      if (lowerText.includes('mil') || lowerText.includes('k')) {
        const numericPrice = parseFloat(price);
        if (!isNaN(numericPrice)) {
          price = (numericPrice * 1000).toString();
        }
      }
    }
    
    // Extraer nombre del producto - más flexible
    const priceKeywords = ['precio', 'cuesta', 'vale', 'costo', 'pesos', 'cop', 'dolares', 'usd'];
    let priceIndex = -1;
    
    for (const keyword of priceKeywords) {
      const index = lowerText.indexOf(keyword);
      if (index > 0 && index > priceIndex) {
        priceIndex = index;
      }
    }
    
    if (priceIndex > 0) {
      productName = lowerText.substring(0, priceIndex).trim();
    } else {
      // Si no hay palabra clave de precio, usar todo el texto como nombre del producto
      productName = lowerText;
    }
    
    // Limpiar y formatear el nombre del producto
    productName = productName
      .replace(/\b(?:producto|productos|nombre|articulo|item|es|una|un)\b/gi, '')
      .replace(/\s+/g, ' ')
      .trim();
    
    // Actualizar el formulario solo si tenemos datos válidos
    if (productName && productName.length > 2) {
      setNewProduct(prev => ({ ...prev, name: productName }));
    }
    if (price && !isNaN(parseFloat(price))) {
      setNewProduct(prev => ({ ...prev, price: price }));
    }
  };

  // Iniciar/parar reconocimiento de voz para productos
  const toggleVoiceRecognition = () => {
    if (!speechRecognition) {
      alert('El reconocimiento de voz no está disponible en este navegador');
      return;
    }
    
    if (isListening) {
      // Detener y procesar el texto final
      speechRecognition.stop();
      setIsListening(false);
      
      // Procesar el texto final si hay algo
      if (voiceText && voiceText.trim()) {
        setTimeout(() => {
          processVoiceInput(voiceText);
        }, 500);
      }
    } else {
      // Resetear flag de procesamiento
      hasBeenProcessedRef.current = false;
      
      // Limpiar texto anterior
      setVoiceText('');
      currentVoiceTextRef.current = '';
      
      // Limpiar formulario de producto
      setTimeout(() => {
        setNewProduct({ name: '', price: '', description: '' });
      }, 0);
      
      try {
        speechRecognition.start();
        setIsListening(true);
        
      } catch (error) {
        errorLog('Error iniciando reconocimiento de voz:', error);
        setIsListening(false);
      }
    }
  };

  // Iniciar/parar reconocimiento de voz para pedidos
  const toggleOrderVoiceRecognition = () => {
    if (!speechRecognition) {
      alert('El reconocimiento de voz no está disponible en este navegador');
      return;
    }
    
    if (isListening) {
      // Detener manualmente
      voiceLog('🛑 Deteniendo reconocimiento de voz manualmente');
      
      // Limpiar timeout si existe
      if (silenceTimeout) {
        clearTimeout(silenceTimeout);
        setSilenceTimeout(null);
      }
      
      // Detener el reconocimiento
      speechRecognition.stop();
      setIsListening(false);
      
      // Procesar el texto final si hay algo
      if (voiceText && voiceText.trim()) {
        voiceLog('🎤 Procesando texto final manual:', voiceText);
        processOrderVoiceInput(voiceText);
      }
      
    } else {
      // Verificar que no esté ya escuchando
      if (speechRecognition.state && speechRecognition.state === 'started') {
        voiceLog('⚠️ Reconocimiento ya está activo, no iniciando nuevo');
        return;
      }
      
      // Resetear flag de procesamiento
      hasBeenProcessedRef.current = false;
      
      // Limpiar texto anterior y formulario
      setVoiceText('');
      currentVoiceTextRef.current = ''; // Resetear ref
      setIsProcessingVoice(false); // Resetear flag de procesamiento
      
      // Limpiar formulario con setTimeout para evitar conflictos de render
      setTimeout(() => {
        setQuickOrderForm({...quickOrderForm, clientName: '', productName: '', price: ''});
      }, 0);
      
      try {
        voiceLog('🎤 Iniciando reconocimiento de voz');
        speechRecognition.start();
        setIsListening(true);
        
      } catch (error) {
        errorLog('❌ Error iniciando reconocimiento de voz:', error);
        setIsListening(false);
        
        // Si el error es que ya está iniciado, intentar detenerlo primero
        if (error.name === 'InvalidStateError' && error.message.includes('already started')) {
          voiceLog('🔄 Reconocimiento ya iniciado, deteniendo primero...');
          try {
            speechRecognition.stop();
            setTimeout(() => {
              try {
                speechRecognition.start();
                setIsListening(true);
                voiceLog('✅ Reconocimiento reiniciado exitosamente');
              } catch (retryError) {
                errorLog('❌ Error en reintento:', retryError);
              }
            }, 100);
          } catch (stopError) {
            errorLog('❌ Error deteniendo reconocimiento:', stopError);
          }
        }
      }
    }
  };

  // Función para procesar el texto de voz para pedidos (cliente, producto, precio)
  const processOrderVoiceInput = (text) => {
    voiceLog('🚀 ========== INICIANDO PROCESAMIENTO DE VOZ ==========');
    voiceLog('🎤 Procesando texto de voz para pedido:', text);
    voiceLog('📝 Texto original:', text);
    voiceLog('🔍 Longitud del texto:', text.length);
    voiceLog('🔍 Tipo de texto:', typeof text);
    
    const lowerText = text.toLowerCase();
    
    let clientName = '';
    let productName = '';
    let price = '';
    
    // Extraer cliente/usuario - patrones flexibles
    const clientPatterns = [
      /(?:cliente|usuario)\s+([^0-9]+?)(?:\s+producto|\s+\d|$)/gi,
      /para\s+([^0-9]+?)(?:\s+producto|\s+\d|$)/gi,
      /([^0-9]+?)\s+(?:quiere|solicita|pide)/gi,
      /(?:cliente|usuario)\s+([a-zA-Z\s]+?)(?=\s+producto|$)/gi,  // Más flexible para nombres con espacios
      /(?:cliente|usuario)\s+([a-zA-Z\s]+?)(?=\s+\d+\s+producto)/gi  // Para casos como "cliente Anita la huerfanita 32 producto"
    ];
    
    voiceLog('🔍 Probando patrones de cliente...');
    for (let i = 0; i < clientPatterns.length; i++) {
      const pattern = clientPatterns[i];
      const matches = [...lowerText.matchAll(pattern)];
      voiceLog(`📋 Patrón ${i + 1}:`, pattern, 'Matches:', matches.length);
      if (matches.length > 0) {
        clientName = matches[0][1].trim();
        voiceLog('👤 Cliente detectado con patrón:', pattern, 'Resultado:', clientName);
        break;
      }
    }
    
    // Si no se detectó cliente con patrones, intentar extraer desde el campo actual
    if (!clientName && quickOrderForm.clientName) {
      clientName = quickOrderForm.clientName;
      voiceLog('🔄 Usando cliente del campo actual:', clientName);
    }
    
    // Extraer producto - patrones flexibles
    const productPatterns = [
      /producto\s+([^0-9]+?)(?:\s+\d|$)/gi,
      /([^0-9]+?)\s+(?:precio|cuesta|vale|costo)/gi,
      /quiero\s+([^0-9]+?)(?:\s+\d|$)/gi,
      /producto\s+([a-zA-Z\s]+?)(?=\s+precio|$)/gi,  // Más flexible para productos con espacios
      /producto\s+([a-zA-Z\s]+?)(?=\s+\d{1,3}(?:,\d{3})*)/gi  // Para casos como "producto zapato tenis talla M color beige precio 285,600"
    ];
    
    voiceLog('🔍 Probando patrones de producto...');
    for (let i = 0; i < productPatterns.length; i++) {
      const pattern = productPatterns[i];
      const matches = [...lowerText.matchAll(pattern)];
      voiceLog(`📋 Patrón producto ${i + 1}:`, pattern, 'Matches:', matches.length);
      if (matches.length > 0) {
        productName = matches[0][1].trim();
        voiceLog('🛍️ Producto detectado:', productName);
        break;
      }
    }
    
    // Extraer precio - usar los mismos patrones que processVoiceInput
    const pricePatterns = [
      /(\d{1,3}(?:,\d{3})*)\s*(?:pesos?|cop|dolares?|usd)/gi,
      /(?:precio|cuesta|vale|costo)\s*(\d{1,3}(?:,\d{3})*)/gi,
      /(\d+(?:\.\d+)?)\s*(?:pesos?|cop|dolares?|usd)/gi,
      /(?:precio|cuesta|vale|costo)\s*(\d+(?:\.\d+)?)/gi,
      /(\d{1,3}(?:,\d{3})*)\s*(?:mil|k)/gi,
      /(\d+(?:\.\d+)?)\s*(?:mil|k)/gi,
      /(\d{1,3}(?:,\d{3})*)/gi,
      /(\d+(?:\.\d+)?)/gi
    ];
    
    for (const pattern of pricePatterns) {
      const matches = [...lowerText.matchAll(pattern)];
      if (matches.length > 0) {
        price = matches[0][1];
        break;
      }
    }
    
    // Procesar precio si se encontró (se procesará después en updates)
    // if (price) {
    //   let finalPrice = price.replace(/,/g, '');
    //   if (lowerText.includes('mil') || lowerText.includes('k')) {
    //     finalPrice = (parseFloat(finalPrice) * 1000).toString();
    //   }
    // }
    
    // Actualizar el formulario con los datos extraídos
    const updates = {};
    if (clientName) {
      updates.clientName = clientName;
      
      // Búsqueda de cliente DESHABILITADA para mejorar rendimiento
      // console.log('🔍 Buscando cliente:', clientName, 'en lista de', clients.length, 'clientes');
      // console.log('📋 Lista de clientes:', clients.map(c => c.username));
      
      // const foundClient = clients.find(client => {
      //   const clientUsername = client.username.toLowerCase();
      //   const searchName = clientName.toLowerCase();
      //   
      //   // Búsqueda más flexible
      //   return clientUsername.includes(searchName) || 
      //          searchName.includes(clientUsername) ||
      //          clientUsername.replace(/[0-9]/g, '') === searchName.replace(/[0-9]/g, '') ||
      //          clientUsername.split('@')[0] === searchName;
      // });
      
      // if (foundClient) {
      //   setQuickOrderClient(foundClient);
      //   console.log('✅ Cliente encontrado:', foundClient.username);
      // } else {
      //   setQuickOrderClient(null);
      //   console.log('⚠️ Cliente no encontrado en la lista. Cliente buscado:', clientName);
      //   console.log('💡 Sugerencia: Verifica que el cliente esté registrado en la sesión');
      // }
    }
    
    if (productName) {
      updates.productName = productName;
    }
    
    if (price) {
      let finalPrice = price.replace(/,/g, '');
      if (lowerText.includes('mil') || lowerText.includes('k')) {
        finalPrice = (parseFloat(price.replace(/,/g, '')) * 1000).toString();
      }
      updates.price = finalPrice;
      voiceLog('💰 Precio final:', finalPrice);
    }
    
    // Si el modal de producto rápido está abierto, actualizar newProduct en lugar de quickOrderForm
    const isProductModalOpen = showQuickProductModalRef.current;
    voiceLog('🔍 Modal de producto rápido abierto?:', isProductModalOpen);
    voiceLog('🔍 Producto detectado?:', productName);
    voiceLog('🔍 Precio detectado?:', price);
    
    if (isProductModalOpen) {
      // Si hay producto y precio, llenar el formulario de producto rápido
      if (productName && price) {
        const productData = {
          name: productName,
          price: price.replace(/,/g, ''),
          description: ''
        };
        voiceLog('✅ Llenando formulario de producto rápido:', productData);
        setNewProduct(productData);
      } else {
        voiceLog('⚠️ No se pudo llenar el formulario - faltan datos:', { productName, price });
      }
    } else {
      // Comportamiento original para pedido rápido
      // Solo actualizar si hay al menos cliente Y producto, o si es una actualización de precio válida
      const hasValidData = (clientName && productName) || 
                          (clientName && price) || 
                          (productName && price) ||
                          (Object.keys(updates).length > 0 && updates.price && updates.price > 0);
      
      if (hasValidData && Object.keys(updates).length > 0) {
        setQuickOrderForm({...quickOrderForm, ...updates});
      }
    }
    
    // Limpiar el texto de voz después de procesarlo
    setVoiceText('');
    setIsProcessingVoice(false); // Resetear flag de procesamiento
  };


  const loadTrips = async () => {
    try {
      const data = await apiService.trip.getAll();
      
      // ✅ VALIDACIÓN: Asegurar que data sea un array
      const tripsArray = Array.isArray(data) ? data : [];
      setTrips(tripsArray);
    } catch (err) {
      errorLog('Error cargando viajes:', err);
      setTrips([]); // Asegurar que siempre sea un array
    }
  };

  const loadEnvivos = async (tripId = null) => {
    try {
      let data;
      if (tripId) {
        // ✅ OPTIMIZACIÓN: Solo cargar envivos del viaje seleccionado
        debugLog('🚀 Cargando envivos solo del viaje:', tripId);
        data = await apiService.envivo.getByTrip(tripId);
      } else {
        // Fallback: cargar todos si no hay tripId
        data = await apiService.envivo.getAll();
      }
      
      // ✅ VALIDACIÓN: Asegurar que data sea un array
      const envivosArray = Array.isArray(data) ? data : [];
      setEnvivos(envivosArray);
    } catch (err) {
      errorLog('Error cargando en vivos:', err);
      setEnvivos([]); // Asegurar que siempre sea un array
    }
  };

  const loadClients = async () => {
    debugLog('🔄 DEBUG: loadClients ejecutándose');
    if (!currentEnvivo) {
      debugLog('🔄 DEBUG: No hay currentEnvivo, limpiando clientes');
      setClients([]);
      return;
    }
    
    try {
      // ✅ OPTIMIZACIÓN: Cargar solo clientes del envivo específico
      debugLog('🔄 DEBUG: Cargando clientes del envivo:', currentEnvivo.id);
      const data = await apiService.client.getByEnvivo(currentEnvivo.id);
      
      debugLog('🔄 DEBUG: Respuesta de API clientes:', data);
      debugLog('🔄 DEBUG: Tipo de data:', typeof data);
      debugLog('🔄 DEBUG: Es array?', Array.isArray(data));
      
      // ✅ VALIDACIÓN: Asegurar que data sea un array
      let clientsArray = [];
      if (Array.isArray(data)) {
        clientsArray = data;
      } else if (data && typeof data === 'object') {
        // Si es un objeto, intentar extraer el array
        if (data.clients && Array.isArray(data.clients)) {
          clientsArray = data.clients;
        } else if (data.data && Array.isArray(data.data)) {
          clientsArray = data.data;
        }
      }
      
      debugLog('🔄 DEBUG: clientsArray final:', clientsArray);
      debugLog('🔄 DEBUG: setClients ejecutándose');
      setClients(clientsArray);
    } catch (err) {
      errorLog('Error cargando clientes:', err);
      // Fallback: cargar todos y filtrar
      try {
        const allData = await apiService.client.getAll();
        const envivoClients = Array.isArray(allData) ? allData.filter(client => client.envivo_id === currentEnvivo.id) : [];
        setClients(envivoClients);
      } catch (fallbackErr) {
        errorLog('Error en fallback de clientes:', fallbackErr);
        setClients([]); // Asegurar que siempre sea un array
      }
    }
  };

  const loadProducts = async () => {
    debugLog('🔄 DEBUG: loadProducts ejecutándose');
    if (!currentEnvivo) {
      debugLog('🔄 DEBUG: No hay currentEnvivo, limpiando productos');
      setProducts([]);
      return;
    }
    
    try {
      // ✅ OPTIMIZACIÓN: Cargar solo productos del envivo específico
      debugLog('🚀 Cargando productos solo del envivo:', currentEnvivo.id);
      const data = await apiService.product.getByEnvivo(currentEnvivo.id);
      
      // ✅ VALIDACIÓN: Asegurar que data sea un array
      const productsArray = Array.isArray(data) ? data : [];
      debugLog('🔄 DEBUG: setProducts ejecutándose');
      setProducts(productsArray);
    } catch (err) {
      errorLog('Error cargando productos:', err);
      // Fallback: cargar todos y filtrar
      try {
        const allData = await apiService.product.getAll();
        const envivoProducts = Array.isArray(allData) ? allData.filter(product => {
          return product.envivo_id === currentEnvivo.id || product.envivoId === currentEnvivo.id;
        }) : [];
        setProducts(envivoProducts);
      } catch (fallbackErr) {
        errorLog('Error en fallback de productos:', fallbackErr);
        setProducts([]); // Asegurar que siempre sea un array
      }
    }
  };

  const loadOrders = async () => {
    debugLog('🔄 DEBUG: loadOrders ejecutándose');
    if (!currentEnvivo) {
      debugLog('🔄 DEBUG: No hay currentEnvivo, limpiando órdenes');
      setOrders([]);
      return;
    }
    
    try {
      // ✅ OPTIMIZACIÓN: Cargar solo órdenes del envivo específico
      debugLog('🚀 Cargando órdenes solo del envivo:', currentEnvivo.id);
      const data = await apiService.order.getByEnvivo(currentEnvivo.id);
      
      // ✅ VALIDACIÓN: Asegurar que data sea un array
      const ordersArray = Array.isArray(data) ? data : [];
      debugLog('🔄 DEBUG: setOrders ejecutándose');
      
      // ✅ Validar min_payment de cada orden
      ordersArray.forEach(order => {
        const isValid = validateMinPayment(order);
        if (!isValid) {
          errorLog(`⚠️ Orden #${order.id} tiene cálculo de min_payment inconsistente`);
        }
      });
      
      setOrders(ordersArray);
      
      // Cargar pagos para cada orden
      await loadOrderPayments(ordersArray);
    } catch (err) {
      errorLog('Error cargando pedidos:', err);
      // Fallback: cargar todos y filtrar
      try {
        const allData = await apiService.order.getAll();
        const envivoOrders = Array.isArray(allData) ? allData.filter(order => {
          return order.envivo_id === currentEnvivo.id || order.envivoId === currentEnvivo.id;
        }) : [];
        setOrders(envivoOrders);
        await loadOrderPayments(envivoOrders);
      } catch (fallbackErr) {
        errorLog('Error en fallback de órdenes:', fallbackErr);
        setOrders([]); // Asegurar que siempre sea un array
      }
    }
  };

  // Función para cargar pagos de las órdenes
  const loadOrderPayments = async (ordersList) => {
    const paymentsData = {};
    
    for (const order of ordersList) {
      try {
        const payments = await apiService.payment.getByOrder(order.id);
        paymentsData[order.id] = payments || [];
      } catch (err) {
        errorLog(`Error cargando pagos para orden ${order.id}:`, err);
        paymentsData[order.id] = [];
      }
    }
    
    setOrderPayments(paymentsData);
  };

  // Estados para carry_on
  const [carryOnUpdatingId, setCarryOnUpdatingId] = useState(null);

  // Funciones auxiliares para calcular min_payment
  /**
   * Calcula carry_on_extra a partir de los items
   * @param {Array} items - Items de la orden
   * @returns {number} Suma de precios de items marcados como maleta
   */
  const calculateCarryOnExtra = (items) => {
    if (!items || !Array.isArray(items)) return 0;
    return items.reduce((sum, item) => {
      if (item.carry_on === 1) {
        return sum + (parseFloat(item.price || 0) * (item.quantity || 1));
      }
      return sum;
    }, 0);
  };

  /**
   * Calcula el abono mínimo de una orden
   * @param {number} totalAmount - Total de la orden
   * @param {number} carryOnExtra - Suma de precios de items marcados como maleta
   * @returns {number} Abono mínimo requerido
   */
  const calculateMinPayment = (totalAmount, carryOnExtra = 0) => {
    return 0.5 * (carryOnExtra + totalAmount);
  };

  /**
   * Valida que el min_payment del backend coincida con el cálculo del frontend
   * @param {Object} order - Orden recibida del backend
   * @returns {boolean} true si los cálculos coinciden
   */
  const validateMinPayment = (order) => {
    if (!order) return true;
    const calculatedCarryOnExtra = calculateCarryOnExtra(order.items || []);
    const calculatedMinPayment = calculateMinPayment(
      parseFloat(order.total_amount || 0),
      calculatedCarryOnExtra
    );
    const backendMinPayment = parseFloat(order.min_payment || calculatedMinPayment);
    
    // Permitir pequeña diferencia por redondeo (0.01)
    const difference = Math.abs(backendMinPayment - calculatedMinPayment);
    const isValid = difference < 0.01;
    
    if (!isValid) {
      warningLog('⚠️ Discrepancia en cálculo de min_payment:', {
        orderId: order.id,
        backend: backendMinPayment,
        frontend: calculatedMinPayment,
        difference: difference
      });
    }
    
    return isValid;
  };

  // Función para calcular el porcentaje pagado de una orden
  const getPaymentStatus = (order) => {
    const payments = orderPayments[order.id] || [];
    const totalOrder = parseFloat(order.total_amount || 0);
    const totalPaid = (payments || []).reduce((sum, payment) => sum + parseFloat(payment.amount || 0), 0);
    const remainingAmount = totalOrder - totalPaid;
    
    // Calcular min_payment: usar el del backend si existe, sino calcularlo
    const carryOnExtra = order.carry_on_extra !== undefined ? parseFloat(order.carry_on_extra || 0) : calculateCarryOnExtra(order.items || []);
    const minPayment = order.min_payment !== undefined ? parseFloat(order.min_payment || 0) : calculateMinPayment(totalOrder, carryOnExtra);
    
    const percentage = totalOrder > 0 ? (totalPaid / totalOrder) * 100 : 0;
    const percentageOfMin = minPayment > 0 ? (totalPaid / minPayment) * 100 : 0;
    
    return {
      totalPaid,
      remainingAmount,
      percentage,
      minPayment,
      percentageOfMin,
      isFullyPaid: percentage >= 100,
      isMinPaid: totalPaid >= minPayment, // Cambiar de isHalfPaid a isMinPaid
      hasPayments: (payments || []).length > 0
    };
  };

  const loadKPIs = useCallback(async () => {
    try {
      // ✅ OPTIMIZADO: Sin logs para mejor performance

      // ✅ CORRECCIÓN: totalClients debe contar los clientes únicos de los pedidos,
      // no solo los clientes registrados en la tabla clients, porque puede haber
      // pedidos de clientes que no están registrados en la tabla clients del envivo
      const uniqueClientsFromOrders = new Set((orders || []).map(o => o.client_username).filter(u => u && u.trim() !== ''));
      const totalClients = uniqueClientsFromOrders.size > 0 ? uniqueClientsFromOrders.size : (clients || []).length;
      const totalProducts = (products || []).length;
      
      const totalOrders = (orders || []).length; // Esto ahora cuenta órdenes reales
      
      const totalRevenue = (orders || []).reduce((sum, order) => sum + parseFloat(order.total_amount || 0), 0);
      
      const clientsWithOrders = uniqueClientsFromOrders.size;
      
      const avgOrderValue = totalOrders > 0 ? totalRevenue / totalOrders : 0;
      
      const orderTotals = (orders || []).map(o => parseFloat(o.total_amount || 0)).filter(t => t > 0);
      const maxOrder = orderTotals.length > 0 ? Math.max(...orderTotals) : 0;
      const minOrder = orderTotals.length > 0 ? Math.min(...orderTotals) : 0;

      const safeKPIs = {
        totalClients,
        clientsWithOrders,
        totalProducts,
        totalOrders,
        avgOrderValue,
        maxOrder,
        minOrder,
        totalRevenue
      };
      setKpis(safeKPIs);
    } catch (err) {
      errorLog('Error calculando KPIs:', err);
      setKpis({
        totalClients: 0,
        clientsWithOrders: 0,
        totalProducts: 0,
        totalOrders: 0,
        avgOrderValue: 0,
        maxOrder: 0,
        minOrder: 0,
        totalRevenue: 0
      });
    }
  }, [clients, products, orders]); // ✅ CORREGIDO: Incluir dependencias para recalculcar KPIs cuando cambien los datos

  const loadAllData = useCallback(async () => {
    setLoading(true);
    try {
      // ✅ OPTIMIZACIÓN: Cargar viajes primero
      await loadTrips();
      
      // ✅ OPTIMIZACIÓN: Cargar solo envivos del viaje seleccionado
      if (selectedTrip) {
        await loadEnvivos(selectedTrip.id);
      } else {
        await loadEnvivos(); // Fallback: cargar todos
      }
      
      // Solo cargar otros datos si hay un en vivo activo
      if (currentEnvivo) {
        await Promise.all([
          loadClients(),
          loadProducts(),
          loadOrders()
        ]);
        
        // Los KPIs se calcularán automáticamente por el useEffect
      }
    } catch (err) {
      setError('Error al cargar datos: ' + err.message);
    } finally {
      setLoading(false);
    }
  }, [selectedTrip, currentEnvivo]); // eslint-disable-line react-hooks/exhaustive-deps

  // Cargar datos al montar el componente
  useEffect(() => {
    // ✅ Cargar viajes primero para que el modal pueda mostrar la lista
    loadTrips();
    
    // ✅ Cargar envivos del viaje seleccionado
    if (selectedTrip) {
      loadEnvivos(selectedTrip.id);
    } else {
      loadEnvivos(); // Fallback: cargar todos
    }
  }, [selectedTrip]); // eslint-disable-line react-hooks/exhaustive-deps

  // ✅ OPTIMIZADO: useEffect CONSOLIDADO para manejar cambios de en vivo activo - SIN showQuickOrderModal en dependencias
  useEffect(() => {
    debugLog('🔄 DEBUG: useEffect ejecutándose - currentEnvivo?.id:', currentEnvivo?.id);
    
    if (currentEnvivo) {
      debugLog('🔄 Cargando datos OPTIMIZADOS para en vivo', currentEnvivo.id);
      // ✅ OPTIMIZACIÓN: Cargar datos del nuevo en vivo activo con endpoints específicos
      const loadData = async () => {
        // ✅ Cargar cache de clientes si está vacío (solo una vez)
        if (allClientsCache.length === 0) {
          try {
            const allClients = await apiService.client.getAll();
            const clientsArray = Array.isArray(allClients) ? allClients : [];
            setAllClientsCache(clientsArray);
            debugLog('✅ Cache de clientes cargado inicialmente:', clientsArray.length, 'clientes');
          } catch (cacheErr) {
            errorLog('⚠️ Error cargando cache de clientes (no crítico):', cacheErr);
            // Continuar con la carga normal aunque falle el cache
          }
        }
        
        const [exportClients, exportProducts, exportOrders] = await Promise.all([
          apiService.client.getByEnvivo(currentEnvivo.id),
          apiService.product.getByEnvivo(currentEnvivo.id),
          apiService.order.getByEnvivo(currentEnvivo.id)
        ]);
        
        // ✅ CORRECCIÓN: La API de clientes devuelve {envivoId, totalClients, clients: Array}
        let clientsArray = [];
        if (Array.isArray(exportClients)) {
          clientsArray = exportClients;
        } else if (exportClients && exportClients.clients && Array.isArray(exportClients.clients)) {
          clientsArray = exportClients.clients;
        }
        
        const productsArray = Array.isArray(exportProducts) ? exportProducts : [];
        const ordersArray = Array.isArray(exportOrders) ? exportOrders : [];
        
        debugLog('📊 DATOS CARGADOS:', {
          clientes: clientsArray.length,
          productos: productsArray.length,
          ordenes: ordersArray.length,
          firstClient: clientsArray[0],
          firstOrder: ordersArray[0]
        });
        
        setClients(clientsArray);
        setProducts(productsArray);
        setOrders(ordersArray);
        
        // ✅ Cargar pagos de las órdenes
        const paymentsData = {};
        for (const order of ordersArray) {
          try {
            const payments = await apiService.payment.getByOrder(order.id);
            paymentsData[order.id] = payments || [];
          } catch (err) {
            errorLog(`Error cargando pagos para orden ${order.id}:`, err);
            paymentsData[order.id] = [];
          }
        }
        setOrderPayments(paymentsData);
        
        debugLog('🔄 DEBUG: Datos cargados, cargando KPIs...');
        // ✅ Cargar KPIs con los datos recién obtenidos
        // ✅ CORRECCIÓN: totalClients debe contar los clientes únicos de los pedidos,
        // no solo los clientes registrados en la tabla clients, porque puede haber
        // pedidos de clientes que no están registrados en la tabla clients del envivo
        const uniqueClientsFromOrders = new Set(ordersArray.map(o => o.client_username).filter(u => u && u.trim() !== ''));
        const totalClients = uniqueClientsFromOrders.size > 0 ? uniqueClientsFromOrders.size : clientsArray.length;
        const totalProducts = productsArray.length;
        const totalOrders = ordersArray.length;
        const totalRevenue = ordersArray.reduce((sum, order) => sum + parseFloat(order.total_amount || 0), 0);
        const clientsWithOrders = uniqueClientsFromOrders.size;
        const avgOrderValue = totalOrders > 0 ? totalRevenue / totalOrders : 0;
        const orderTotals = ordersArray.map(o => parseFloat(o.total_amount || 0)).filter(t => t > 0);
        const maxOrder = orderTotals.length > 0 ? Math.max(...orderTotals) : 0;
        const minOrder = orderTotals.length > 0 ? Math.min(...orderTotals) : 0;
        
        setKpis({
          totalClients,
          clientsWithOrders,
          totalProducts,
          totalOrders,
          avgOrderValue,
          maxOrder,
          minOrder,
          totalRevenue
        });
      };
      
      loadData().catch(error => {
        errorLog('Error cargando datos:', error);
      });
    } else {
      debugLog('🔄 DEBUG: No hay en vivo activo, limpiando datos...');
      // Limpiar datos si no hay en vivo activo
      setClients([]);
      setProducts([]);
      setOrders([]);
      setKpis({
        totalClients: 0,
        clientsWithOrders: 0,
        totalProducts: 0,
        totalOrders: 0,
        avgOrderValue: 0,
        maxOrder: 0,
        minOrder: 0,
        totalRevenue: 0
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentEnvivo?.id]); // ✅ SIN showQuickOrderModal para mantener performance

  // ✅ DEBUG: Validar cache de clientes en consola (deshabilitado)
  // useEffect(() => {
  //   console.log('📋 Cache de clientes (allClientsCache):', allClientsCache);
  //   console.log('📊 Total clientes en cache:', allClientsCache.length);
  //   if (allClientsCache.length > 0) {
  //     console.log('✅ Primeros 5 clientes en cache:', allClientsCache.slice(0, 5).map(c => ({
  //       id: c.id,
  //       username: c.username,
  //       name: c.name || c.full_name
  //     })));
  //   } else {
  //     console.log('⚠️ Cache de clientes está vacío');
  //   }
  // }, [allClientsCache]);

  // Limpiar timeouts al desmontar - DESHABILITADO (no hay timeouts)
  // useEffect(() => {
  //   return () => {
  //     if (clientSearchDebounce) clearTimeout(clientSearchDebounce);
  //     if (productSearchDebounce) clearTimeout(productSearchDebounce);
  //   };
  // }, [clientSearchDebounce, productSearchDebounce]);

  // Cerrar menú de copiar al hacer clic fuera
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (copyMenuOrderId !== null && !event.target.closest('.copy-menu-container')) {
        setCopyMenuOrderId(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [copyMenuOrderId]);

  // Atajos de teclado para acciones rápidas
  useEffect(() => {
    const handleKeyDown = (event) => {
      // Detectar si es Mac (Meta key) o Windows/Linux (Ctrl key)
      const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0;
      const modifierKey = isMac ? event.metaKey : event.ctrlKey;
      const shiftKey = event.shiftKey;
      
      // Solo procesar si la tecla modificadora y Shift están presionadas
      if (!modifierKey || !shiftKey) return;
      
      const target = event.target;
      const isInput = target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable;
      
      // Si estamos en un input, no procesar atajos (excepto algunos casos específicos)
      if (isInput && !target.closest('[data-allow-shortcuts="true"]')) return;
      
      switch (event.key.toLowerCase()) {
        case 'a': // Ctrl/Cmd + Shift + A: Agregar Clientes
          event.preventDefault();
          setShowClientModal(true);
          break;
        case 'o': // Ctrl/Cmd + Shift + O: Pedido Rápido (Order)
          event.preventDefault();
          if ((clients || []).length === 0) {
            setError('Primero debes agregar clientes al en vivo');
            return;
          }
          setQuickOrderClient(null);
          setQuickOrderForm({ productName: '', price: '', clientName: '' });
          setShowQuickOrderModal(true);
          break;
        case 't': // Ctrl/Cmd + Shift + T: Producto Rápido (Product)
          event.preventDefault();
          setShowQuickProductModal(true);
          break;
        case 'n': // Ctrl/Cmd + Shift + N: Pedido Manual (Manual)
          event.preventDefault();
          if ((clients || []).length === 0) {
            setError('Primero debes agregar clientes al en vivo');
            return;
          }
          setShowOrderModal(true);
          break;
        default:
          // No hacer nada para otras teclas
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [clients]); // Dependencia de clients para verificar si hay clientes

  // Mostrar/ocultar botón "Volver arriba" y buscador móvil según scroll
  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      const isMobileOrTablet = window.innerWidth < 1024; // Mobile y tablet/iPad
      
      // Umbral para mostrar el botón y buscador: 200px desde el inicio
      // Esto corresponde aproximadamente a cuando se llega a los primeros cards de pedidos
      const SHOW_THRESHOLD = 200;
      
      if (currentScrollY > SHOW_THRESHOLD) {
        // Mostrar botón "Ir Arriba" cuando pasamos el umbral
        setShowScrollTop(true);
        
        // En mobile y tablet, mostrar el buscador también
        if (isMobileOrTablet) {
          setShowMobileSearch(true);
        } else {
          // En desktop siempre mostrar el buscador
          setShowMobileSearch(true);
        }
      } else {
        // Ocultar cuando estamos arriba del umbral
        setShowScrollTop(false);
        
        // En mobile y tablet, ocultar el buscador cuando estamos arriba
        if (isMobileOrTablet) {
          setShowMobileSearch(false);
        } else {
          // En desktop siempre mostrar el buscador
          setShowMobileSearch(true);
        }
      }
      
      // Detectar cuando el segundo menú debe volverse sticky
      // El segundo menú debe volverse sticky cuando el scroll supera su posición inicial
      if (secondMenuRef.current) {
        const menuRect = secondMenuRef.current.getBoundingClientRect();
        // Obtener la altura real del primer menú desde el DOM
        const firstMenu = document.querySelector('nav.fixed');
        const firstMenuHeight = firstMenu ? firstMenu.getBoundingClientRect().height : 65; // Altura real medida: 65px
        // El menú está sticky cuando su posición top es igual o menor a la altura del primer menú
        // Usamos un umbral más estricto para detectar cuando está pegado
        const shouldBeSticky = menuRect.top <= firstMenuHeight + 2; // +2px para tolerancia
        setIsSecondMenuSticky(shouldBeSticky);
        
        // Sincronizar dinámicamente el top con la altura real del primer menú
        if (firstMenu && secondMenuRef.current) {
          const actualFirstMenuHeight = firstMenu.getBoundingClientRect().height;
          const currentTop = parseInt(window.getComputedStyle(secondMenuRef.current).top);
          if (Math.abs(currentTop - actualFirstMenuHeight) > 1) {
            // Ajustar el top si hay una diferencia mayor a 1px
            secondMenuRef.current.style.top = `${actualFirstMenuHeight}px`;
          }
        }
      }
      
      lastScrollYRef.current = currentScrollY;
    };

    // Inicializar lastScrollYRef
    lastScrollYRef.current = window.scrollY;
    
    window.addEventListener('scroll', handleScroll, { passive: true });
    // Ejecutar una vez al inicio
    handleScroll();
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []); // Sin dependencias para que solo se registre una vez

  // Función para limpiar resaltados anteriores
  const clearHighlights = useCallback(() => {
    const containers = document.querySelectorAll('.search-highlight-container');
    containers.forEach(container => {
      const text = container.textContent;
      const textNode = document.createTextNode(text);
      if (container.parentNode) {
        container.parentNode.replaceChild(textNode, container);
      }
    });
    // Normalizar el DOM después de limpiar
    document.body.normalize();
  }, []);

  // Función para resaltar texto en la página
  const highlightText = useCallback((term) => {
    if (!term || typeof window === 'undefined') return;
    
    // Limpiar resaltados anteriores
    clearHighlights();

    // Primero verificar si el término existe en el contenido usando window.find
    let windowFound = false;
    try {
      windowFound = window.find(term, false, false, true, false, true, false);
    } catch (e) {
      // Ignorar errores
    }

    // Si window.find no encuentra nada, mostrar alerta
    if (!windowFound) {
      // Verificar manualmente si el texto existe en el DOM
      const allText = (document.body.innerText || document.body.textContent || '').toLowerCase();
      if (!allText.includes(term.toLowerCase())) {
        alert(`No se encontraron coincidencias para "${term}".`);
        return;
      }
    }

    // Buscar y resaltar todas las ocurrencias en nodos de texto
    const walker = document.createTreeWalker(
      document.body,
      NodeFilter.SHOW_TEXT,
      {
        acceptNode: (node) => {
          // Excluir nodos dentro de inputs, textareas, scripts, styles, y contenedores de resaltado
          const parent = node.parentElement;
          if (!parent) return NodeFilter.FILTER_REJECT;
          
          if (
            parent.tagName === 'INPUT' || 
            parent.tagName === 'TEXTAREA' || 
            parent.tagName === 'SCRIPT' || 
            parent.tagName === 'STYLE' ||
            parent.closest('.search-highlight-container') ||
            parent.closest('nav') || // Excluir el menú de navegación
            parent.closest('button') // Excluir botones
          ) {
            return NodeFilter.FILTER_REJECT;
          }
          
          // Solo aceptar nodos que tengan texto y no estén vacíos
          const text = node.textContent.trim();
          if (text.length === 0) {
            return NodeFilter.FILTER_REJECT;
          }
          
          return NodeFilter.FILTER_ACCEPT;
        }
      },
      false
    );

    const textNodes = [];
    let node;
    // eslint-disable-next-line no-cond-assign
    while ((node = walker.nextNode())) {
      textNodes.push(node);
    }

    const escapedTerm = term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const regex = new RegExp(`(${escapedTerm})`, 'gi');
    let found = false;
    
    // Procesar los nodos en orden inverso para evitar problemas con el DOM
    for (let i = textNodes.length - 1; i >= 0; i--) {
      const textNode = textNodes[i];
      const text = textNode.textContent;
      
      // Resetear el regex para cada prueba
      regex.lastIndex = 0;
      if (regex.test(text)) {
        found = true;
        regex.lastIndex = 0; // Resetear de nuevo para el replace
        const highlightedText = text.replace(regex, '<mark class="search-highlight bg-yellow-300 text-gray-900 px-0.5 rounded">$1</mark>');
        
        // Crear un fragmento con el contenido resaltado
        const wrapper = document.createElement('span');
        wrapper.className = 'search-highlight-container';
        wrapper.innerHTML = highlightedText;
        
        // Reemplazar el nodo de texto con el wrapper
        if (textNode.parentNode) {
          textNode.parentNode.replaceChild(wrapper, textNode);
        }
      }
    }

    // Si window.find encontró algo pero no pudimos resaltarlo, al menos navegar a la primera ocurrencia
    if (windowFound && !found) {
      // El texto existe pero no lo encontramos en nodos de texto directos
      // Esto puede pasar con contenido dinámico de React
      // En este caso, confiamos en window.find para la navegación
    } else if (found) {
      // Si encontramos algo, también usar window.find para navegar a la primera ocurrencia
      try {
        window.find(term, false, false, true, false, true, false);
      } catch (e) {
        // Ignorar errores de window.find
      }
    }
  }, [clearHighlights]);

  const handleMobileQuickSearch = useCallback(() => {
    if (typeof window === 'undefined') return;
    const term = mobileSearchTerm.trim();
    if (!term) {
      // Limpiar resaltados si el término está vacío
      clearHighlights();
      return;
    }

    // Usar resaltado manual
    highlightText(term);
  }, [mobileSearchTerm, highlightText, clearHighlights]);

  // useEffect para inicializar orderForm cuando se cargan los productos
  // useEffect DESHABILITADO PARA RENDIMIENTO - causaba re-renders masivos
  // useEffect(() => {
  //   setOrderForm({
  //     clientId: '',
  //     products: products.map(p => ({
  //       id: p.id,
  //       name: p.name,
  //       price: p.base_price || p.basePrice || p.price,
  //       quantity: 1,
  //       selected: false
  //     }))
  //   });
  // }, [products]);

  // Crear nuevo en vivo
  const createEnvivo = useCallback(async () => {
    setShowEnvivoModal(true);
  }, []);

  // Función para manejar el envío del formulario de en vivo
  const handleEnvivoSubmit = async (e) => {
        e.preventDefault();
    if (!envivoForm.name.trim() || !envivoForm.tripId) {
      setError('Nombre de en vivo y viaje son obligatorios');
      return;
    }
    
    setLoading(true);
    try {
      const newEnvivo = await apiService.envivo.create({
        name: envivoForm.name,
        tripId: parseInt(envivoForm.tripId)
      });
      
      // ✅ Cargar todos los clientes y guardarlos en cache para autocompletado rápido
      try {
        const allClients = await apiService.client.getAll();
        const clientsArray = Array.isArray(allClients) ? allClients : [];
        setAllClientsCache(clientsArray);
        debugLog('✅ Cache de clientes actualizado:', clientsArray.length, 'clientes cargados');
      } catch (cacheErr) {
        errorLog('⚠️ Error cargando cache de clientes (no crítico):', cacheErr);
        // No fallar la creación del envivo si falla el cache
      }
      
      await loadEnvivos(selectedTrip?.id);
      setCurrentEnvivo(newEnvivo);
      onEnvivoSelect(newEnvivo); // Actualizar estado global
      setShowEnvivoModal(false);
      setEnvivoForm({ name: '', tripId: '' });
      setError('');
    } catch (err) {
      setError('Error al crear en vivo: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  // Cerrar en vivo actual
  const closeEnvivo = async () => {
    if (!currentEnvivo) {
      window.alert('❌ No hay ningún en vivo activo para cerrar');
      return;
    }
    
    setShowCloseEnvivoModal(true);
  };


  // Reabrir en vivo cerrado
  const reopenEnvivo = async (envivo) => {
    if (!envivo || envivo.status === 'active') {
      window.alert('❌ Este en vivo ya está activo o no existe');
      return;
    }
    
    const confirmReopen = window.confirm(`¿Estás seguro de que quieres reabrir el en vivo "${envivo.name}"?\n\nEsto lo marcará como activo y podrás agregar más pedidos.`);
    if (!confirmReopen) return;
    
    setLoading(true);
    try {
      await apiService.envivo.reopen(envivo.id);
      await loadEnvivos(selectedTrip?.id);
      const updatedEnvivo = { ...envivo, status: 'active' };
      setCurrentEnvivo(updatedEnvivo);
      onEnvivoSelect(updatedEnvivo); // Actualizar estado global
      
      // Cargar datos del en vivo reabierto
      await Promise.all([
        loadClients(),
        loadProducts(),
        loadOrders()
      ]);
      
      debugLog('✅ En vivo reabierto exitosamente');
      
    } catch (err) {
      setError('Error al reabrir en vivo: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  // Funciones para acciones de clientes
  const handleEditClient = (client) => {
    setEditingClient(client);
    setEditClientForm({
      username: client.username,
      name: client.name || ''
    });
    setShowEditClientModal(true);
  };

  const handleUpdateClient = async () => {
    if (!editingClient || !editClientForm.username.trim()) {
      setError('Username es obligatorio');
      return;
    }

    setLoading(true);
    try {
      await apiService.client.update(editingClient.id, {
        username: editClientForm.username,
        name: editClientForm.name
      });
      
      await loadClients();
      setShowEditClientModal(false);
      setEditingClient(null);
      setEditClientForm({ username: '', name: '' });
      setError('');
    } catch (err) {
      setError('Error al actualizar cliente: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClient = async (clientId) => {
    // Buscar el cliente que se quiere eliminar
    const clientToDelete = clients.find(c => c.id === clientId);
    if (!clientToDelete) {
      setError('Cliente no encontrado');
      return;
    }

    // Verificar si el cliente tiene órdenes
    const clientOrders = (orders || []).filter(order => 
      order.client_username === clientToDelete.username ||
      order.clientUsername === clientToDelete.username ||
      order.client_id === clientId
    );

    if ((clientOrders || []).length > 0) {
      setError(`No se puede eliminar este cliente porque tiene ${(clientOrders || []).length} orden(es) registrada(s). Por favor, elimina las órdenes primero.`);
      return;
    }

    if (!window.confirm(`¿Estás seguro de que quieres eliminar al cliente "${clientToDelete.username}"?`)) {
      return;
    }

    setLoading(true);
    try {
      debugLog('🗑️ Eliminando cliente:', clientToDelete.username);
      await apiService.client.delete(clientId);
      await loadClients();
      setError('');
      debugLog('✅ Cliente eliminado exitosamente');
    } catch (err) {
      errorLog('❌ Error al eliminar cliente:', err);
      setError('Error al eliminar cliente: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  // Funciones para acciones de productos - HABILITADAS
  const handleEditProduct = (product) => {
    setEditingProduct(product);
    setEditProductForm({
      name: product.name,
      basePrice: product.base_price || product.basePrice || product.price || '',
      description: product.description || ''
    });
    setShowEditProductModal(true);
  };

  const handleUpdateProduct = async () => {
    if (!editingProduct || !editProductForm.name.trim() || !editProductForm.basePrice) {
      setError('Nombre y precio son obligatorios');
      return;
    }

    setLoading(true);
    try {
      // Verificar si el nombre del producto cambió
      const nameChanged = editingProduct.name !== editProductForm.name;
      const priceChanged = (editingProduct.base_price || editingProduct.basePrice || editingProduct.price) !== parseFloat(editProductForm.basePrice);
      
      productLog('🔄 Actualizando producto:', {
        id: editingProduct.id,
        oldName: editingProduct.name,
        newName: editProductForm.name,
        oldPrice: editingProduct.base_price || editingProduct.basePrice || editingProduct.price,
        newPrice: parseFloat(editProductForm.basePrice),
        nameChanged,
        priceChanged
      });

      // Actualizar el producto
      await apiService.product.update(editingProduct.id, {
        name: editProductForm.name,
        basePrice: parseFloat(editProductForm.basePrice),
        description: editProductForm.description
      });
      
      // Si el nombre o precio cambió, actualizar todas las órdenes que usan este producto
      if (nameChanged || priceChanged) {
        productLog('🔄 Producto cambió, actualizando órdenes...', { nameChanged, priceChanged });
        
        // Buscar todas las órdenes que usan este producto (usar el nombre original)
        const ordersToUpdate = (orders || []).filter(order => 
          order.items && order.items.some(item => 
            item.productName === editingProduct.name
          )
        );
        
        productLog(`🔄 Encontradas ${(ordersToUpdate || []).length} órdenes para actualizar`);
        productLog('📋 Órdenes encontradas:', (ordersToUpdate || []).map(o => ({ id: o.id, client: o.client_username || o.clientUsername, items: o.items })));
        
        // Actualizar cada orden
        let successCount = 0;
        let errorCount = 0;
        
        for (const order of ordersToUpdate) {
          orderLog(`🔄 Actualizando orden ${order.id} del cliente ${order.client_username || order.clientUsername}`);
          
          const updatedItems = (order.items || []).map(item => {
            if (item.productName === editingProduct.name) {
              const updatedItem = { ...item };
              
              // Actualizar nombre si cambió
              if (nameChanged) {
                updatedItem.productName = editProductForm.name;
                productLog(`📝 Actualizando nombre: "${item.productName}" → "${editProductForm.name}"`);
              }
              
              // Actualizar precio si cambió
              if (priceChanged) {
                updatedItem.price = parseFloat(editProductForm.basePrice);
                productLog(`💰 Actualizando precio: ${item.price} → ${parseFloat(editProductForm.basePrice)}`);
              }
              
              return updatedItem;
            }
            return item;
          });
          
          // Calcular nuevo total
          const newTotal = updatedItems.reduce((sum, item) => sum + (item.price * item.quantity), 0);
          orderLog(`💵 Nuevo total calculado: ${newTotal}`);
          
          try {
            // Preparar datos de actualización para esta orden específica
            const orderUpdateData = {
              items: updatedItems,
              total_amount: newTotal,
              envivo_id: currentEnvivo.id,
              client_username: order.client_username || order.clientUsername
            };
            
            orderLog(`🔍 Datos de actualización para orden ${order.id}:`, orderUpdateData);
            await apiService.order.update(order.id, orderUpdateData);
            orderLog(`✅ Orden ${order.id} actualizada exitosamente`);
            successCount++;
            
          } catch (error) {
            errorLog(`❌ Error actualizando orden ${order.id}:`, error);
            errorCount++;
          }
        }
        
        // Mostrar mensaje de resultado al usuario
        if (successCount > 0 && errorCount === 0) {
          const affectedClients = (ordersToUpdate || []).map(order => order.client_username || order.clientUsername).join(', ');
          alert(`✅ PRODUCTO Y ÓRDENES ACTUALIZADOS\n\n🎯 El producto "${editingProduct.name}" se actualizó correctamente.\n\n📋 Órdenes actualizadas:\n- Clientes: ${affectedClients}\n- Total de órdenes: ${successCount}\n- Nuevo precio: $${editProductForm.basePrice}\n\n🎊 Los KPIs y totales se han recalculado automáticamente.`);
        } else if (successCount > 0 && errorCount > 0) {
          alert(`⚠️ ACTUALIZACIÓN PARCIAL\n\n✅ El producto se actualizó correctamente.\n\n📊 Resultados:\n- Órdenes actualizadas: ${successCount}\n- Órdenes con error: ${errorCount}\n\n🔍 Revisa la consola para más detalles.`);
        } else {
          alert(`❌ ERROR EN ACTUALIZACIÓN\n\n✅ El producto se actualizó correctamente.\n\n❌ No se pudieron actualizar las órdenes relacionadas.\n\n🔍 Revisa la consola para más detalles.`);
        }
        
        // Recargar datos para sincronizar la UI
        await Promise.all([
          loadOrders(),
          loadKPIs()
        ]);
      }

      // Recargar todos los datos
      await Promise.all([
        loadProducts(),
        loadOrders(),
        loadKPIs()
      ]);
      
      setShowEditProductModal(false);
      setEditingProduct(null);
      setEditProductForm({ name: '', basePrice: '', description: '' });
      setError('');
      
      productLog('✅ Producto actualizado exitosamente con todas las dependencias');
    } catch (err) {
      errorLog('❌ Error al actualizar producto:', err);
      setError('Error al actualizar producto: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteProduct = async (productId) => {
    // Buscar el producto que se quiere eliminar
    const productToDelete = products.find(p => p.id === productId);
    if (!productToDelete) {
      setError('Producto no encontrado');
      return;
    }

    // Verificar si el producto está siendo usado en algún pedido
    const ordersUsingProduct = (orders || []).filter(order => 
      order.items && order.items.some(item => 
        item.productName === productToDelete.name
      )
    );

    if ((ordersUsingProduct || []).length > 0) {
      const clientNames = (ordersUsingProduct || []).map(order => order.client_username || order.clientUsername).join(', ');
      setError(`No se puede eliminar este producto porque está siendo usado en pedidos de los siguientes clientes: ${clientNames}. Total de órdenes afectadas: ${(ordersUsingProduct || []).length}`);
      return;
    }

    if (!window.confirm(`¿Estás seguro de que quieres eliminar el producto "${productToDelete.name}"?`)) {
      return;
    }

    setLoading(true);
    try {
      productLog('🗑️ Eliminando producto:', productToDelete.name);
      await apiService.product.delete(productId);
      await loadProducts();
      setError('');
      productLog('✅ Producto eliminado exitosamente');
    } catch (err) {
      errorLog('❌ Error al eliminar producto:', err);
      setError('Error al eliminar producto: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  // Función para eliminar item específico de una orden
  const handleRemoveItemFromOrder = async (orderId, itemIndex) => {
    const order = orders.find(o => o.id === orderId);
    if (!order) {
      setError('Orden no encontrada');
      return;
    }

    const item = order.items[itemIndex];
    if (!item) {
      setError('Item no encontrado');
      return;
    }

    const itemTotal = parseFloat(item.price) * (item.quantity || 1);
    const newOrderTotal = parseFloat(order.total_amount || 0) - itemTotal;

    if (!window.confirm(
      `¿Estás seguro de que quieres eliminar este producto de la orden?\n\n` +
      `Producto: ${item.productName}\n` +
      `Precio: $${parseFloat(item.price).toLocaleString('es-CO')}\n` +
      `Cantidad: ${item.quantity || 1}\n` +
      `Subtotal: $${itemTotal.toLocaleString('es-CO')}\n\n` +
      `El total de la orden cambiará de $${parseFloat(order.total_amount || 0).toLocaleString('es-CO')} a $${newOrderTotal.toLocaleString('es-CO')}`
    )) {
      return;
    }

    setLoading(true);
    try {
      orderLog('🗑️ Eliminando item de orden:', { orderId, itemIndex, item: item.productName });
      await apiService.order.removeItem(orderId, itemIndex);
      
      // Recargar datos
      await Promise.all([
        loadOrders(),
        loadKPIs()
      ]);
      
      setError('');
      orderLog('✅ Item eliminado exitosamente');
    } catch (err) {
      errorLog('❌ Error al eliminar item:', err);
      setError('Error al eliminar producto de la orden: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  // Función para abrir modal de gestión de productos de orden
  const handleOpenOrderProductsModal = (order) => {
    setSelectedOrderForProducts(order);
    setShowOrderProductsModal(true);
  };

  // Función para abrir modal de edición de item de orden
  const handleEditItemInOrder = (orderId, itemIndex, item) => {
    setEditingOrderItem({ orderId, itemIndex, item });
    setEditOrderItemForm({
      productName: item.productName,
      price: item.price.toString(),
      quantity: (item.quantity || 1).toString()
    });
    setShowEditOrderItemModal(true);
  };

  // Función para actualizar item específico de una orden
  const handleUpdateItemInOrder = async (orderId, itemIndex, updatedItem) => {
    const order = orders.find(o => o.id === orderId);
    if (!order) {
      setError('Orden no encontrada');
      return;
    }

    const currentItem = order.items[itemIndex];
    if (!currentItem) {
      setError('Item no encontrado');
      return;
    }

    // Calcular nuevos totales
    const oldItemTotal = parseFloat(currentItem.price) * (currentItem.quantity || 1);
    const newItemTotal = parseFloat(updatedItem.price) * (updatedItem.quantity || 1);
    const totalDifference = newItemTotal - oldItemTotal;
    const newOrderTotal = parseFloat(order.total_amount || 0) + totalDifference;

    if (!window.confirm(
      `¿Estás seguro de que quieres actualizar este producto?\n\n` +
      `Producto: ${updatedItem.productName}\n` +
      `Precio: $${parseFloat(updatedItem.price).toLocaleString('es-CO')} (antes: $${parseFloat(currentItem.price).toLocaleString('es-CO')})\n` +
      `Cantidad: ${updatedItem.quantity || 1} (antes: ${currentItem.quantity || 1})\n` +
      `Subtotal: $${newItemTotal.toLocaleString('es-CO')} (antes: $${oldItemTotal.toLocaleString('es-CO')})\n\n` +
      `El total de la orden cambiará de $${parseFloat(order.total_amount || 0).toLocaleString('es-CO')} a $${newOrderTotal.toLocaleString('es-CO')}`
    )) {
      return;
    }

    setLoading(true);
    try {
      orderLog('✏️ Actualizando item de orden:', { orderId, itemIndex, updatedItem });
      await apiService.order.updateItem(orderId, itemIndex, updatedItem);
      
      // Recargar datos
      await Promise.all([
        loadOrders(),
        loadKPIs()
      ]);
      
      setError('');
      orderLog('✅ Item actualizado exitosamente');
    } catch (err) {
      errorLog('❌ Error al actualizar item:', err);
      setError('Error al actualizar producto de la orden: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  // Función para guardar cambios del item editado
  const handleSaveOrderItemEdit = async () => {
    if (!editingOrderItem) return;

    const { orderId, itemIndex } = editingOrderItem;
    
    const updatedItem = {
      productName: editOrderItemForm.productName,
      price: parseFloat(editOrderItemForm.price),
      quantity: parseInt(editOrderItemForm.quantity) || 1
    };

    await handleUpdateItemInOrder(orderId, itemIndex, updatedItem);
    
    // Cerrar modal
    setShowEditOrderItemModal(false);
    setEditingOrderItem(null);
    setEditOrderItemForm({ productName: '', price: '', quantity: '' });
  };

  // Función para toggle carry_on (viaja en maleta) de un item
  const handleToggleCarryOn = async (itemId, currentCarryOn) => {
    if (!itemId) {
      errorLog('❌ Item ID no encontrado para toggle carry_on');
      return;
    }

    setCarryOnUpdatingId(itemId);
    try {
      const newCarryOn = !currentCarryOn;
      orderLog('🚀 Actualizando carry_on:', { itemId, carryOn: newCarryOn });
      
      const result = await apiService.order.updateCarryOn(itemId, newCarryOn);
      
      // Validar que los cálculos coincidan
      if (result.order) {
        const isValid = validateMinPayment(result.order);
        if (!isValid) {
          errorLog('⚠️ Discrepancia en cálculo de min_payment detectada');
        }
      }
      
      // Actualizar la orden en el estado local
      await loadOrders();
      await loadOrderPayments(orders);
      
      orderLog('✅ carry_on actualizado exitosamente');
    } catch (err) {
      errorLog('❌ Error al actualizar carry_on:', err);
      setError('Error al actualizar estado "viaja en maleta": ' + err.message);
    } finally {
      setCarryOnUpdatingId(null);
    }
  };

  // Función para eliminar orden
  const handleDeleteOrder = async (orderId) => {
    // Buscar la orden que se quiere eliminar
    const orderToDelete = orders.find(o => o.id === orderId);
    if (!orderToDelete) {
      setError('Orden no encontrada');
      return;
    }

    const clientUsername = orderToDelete.client_username || orderToDelete.clientUsername;
    const totalAmount = parseFloat(orderToDelete.total_amount || 0);
    const itemsCount = (orderToDelete.items || []).length || 0;

    // Verificar si la orden tiene pagos registrados
    const orderHasPayments = orderPayments[orderId] && (orderPayments[orderId] || []).length > 0;
    
    if (orderHasPayments) {
      const totalPaid = (orderPayments[orderId] || []).reduce((sum, payment) => sum + parseFloat(payment.amount || 0), 0);
      if (!window.confirm(
        `⚠️ ATENCIÓN: Esta orden tiene pagos registrados por $${totalPaid.toLocaleString('es-CO')}.\n\n` +
        `Orden: @${clientUsername}\n` +
        `Total: $${totalAmount.toLocaleString('es-CO')}\n` +
        `Productos: ${itemsCount}\n` +
        `Pagado: $${totalPaid.toLocaleString('es-CO')}\n\n` +
        `¿Estás seguro de que quieres eliminar esta orden? Los pagos asociados también se eliminarán.`
      )) {
        return;
      }
    } else {
      if (!window.confirm(
        `¿Estás seguro de que quieres eliminar esta orden?\n\n` +
        `Cliente: @${clientUsername}\n` +
        `Total: $${totalAmount.toLocaleString('es-CO')}\n` +
        `Productos: ${itemsCount}`
      )) {
        return;
      }
    }

    setLoading(true);
    try {
      orderLog('🗑️ Eliminando orden:', { orderId, client: clientUsername, total: totalAmount });
      await apiService.order.cancel(orderId);
      
      // Limpiar pagos de la orden eliminada inmediatamente
      setOrderPayments(prev => {
        const updated = { ...prev };
        delete updated[orderId];
        return updated;
      });
      
      // Eliminar la orden del estado local inmediatamente
      setOrders(prev => prev.filter(order => order.id !== orderId));
      
      // Recargar datos completos para asegurar sincronización
      debugLog('🔄 Recargando datos después de eliminar orden...');
      await Promise.all([
        loadOrders(),    // Recargar órdenes desde el backend
        loadKPIs(),      // Recalcular KPIs
        loadProducts(),  // Recargar productos (por si hay cambios en los indicadores)
        loadClients()    // Recargar clientes (por si hay cambios en los indicadores)
      ]);
      
      setError('');
      orderLog('✅ Orden eliminada exitosamente');
    } catch (err) {
      errorLog('❌ Error al eliminar orden:', err);
      setError('Error al eliminar orden: ' + err.message);
      
      // Si hay error, recargar datos para restaurar el estado
      try {
        await loadOrders();
        await loadKPIs();
      } catch (reloadErr) {
        errorLog('❌ Error al recargar datos después del error:', reloadErr);
      }
    } finally {
      setLoading(false);
    }
  };

  // Funciones para modal de carga de pago
  const handleLoadPayment = async (order) => {
    setPaymentOrder(order);
    setPaymentForm({ bank: '', amount: '' }); // Campo vacío, total como placeholder
    setPaymentImage(null);
    setPaymentImagePreview(null);
    setSendExcessToBag(false);
    setAdvanceBalance(0);
    setShowPaymentModal(true);
    
    // 🔍 DEBUG: Verificar datos antes de consultar bolsa
    debugLog('🔍 DEBUG BOLSA - handleLoadPayment:', {
      orderId: order.id,
      client_username: order.client_username,
      client_id: order.client_id,
      clientId: order.clientId, // Verificar si viene en camelCase
      selectedTrip: selectedTrip,
      selectedTripId: selectedTrip?.id,
      tieneClientId: !!(order.client_id || order.clientId),
      tieneSelectedTrip: !!selectedTrip?.id
    });
    
    // Cargar saldo disponible de la bolsa si tenemos client_id y trip_id
    // Intentar ambos formatos: client_id (snake_case) o clientId (camelCase)
    const clientId = order.client_id || order.clientId;
    const tripId = selectedTrip?.id;
    
    if (clientId && tripId) {
      debugLog(`🚀 Intentando obtener bolsa para cliente ${order.client_username} (ID: ${clientId}) en viaje ${tripId}`);
      setLoadingAdvanceBalance(true);
      try {
        const balanceData = await apiService.advancePayment.getBalance(clientId, tripId);
        debugLog('✅ Respuesta de bolsa recibida:', balanceData);
        const totalAvailable = balanceData.summary?.total_available || 0;
        setAdvanceBalance(totalAvailable);
        debugLog('✅ Saldo disponible en bolsa:', totalAvailable);
      } catch (err) {
        errorLog('❌ Error obteniendo saldo de bolsa:', err);
        // No mostrar error al usuario, solo dejar en 0
        setAdvanceBalance(0);
      } finally {
        setLoadingAdvanceBalance(false);
      }
    } else {
      debugLog('⚠️ No se puede obtener bolsa - faltan datos:', {
        clientId: clientId,
        tripId: tripId,
        tieneClientId: !!clientId,
        tieneTripId: !!tripId
      });
    }
  };
  
  // Función para calcular excedente de pago
  const calculatePaymentExcess = (paymentAmount, order) => {
    if (!order || !paymentAmount) {
      return { neededForMin: 0, excess: 0, canSendToBag: false };
    }
    
    const minPayment = parseFloat(order.min_payment || 0);
    const currentPaid = parseFloat(order.total_paid || 0);
    
    // Calcular cuánto se necesita para cubrir el mínimo
    const neededForMin = Math.max(0, minPayment - currentPaid);
    
    // Calcular excedente
    const excess = paymentAmount - neededForMin;
    
    return {
      neededForMin,
      excess: Math.max(0, excess),
      canSendToBag: excess > 0 && paymentAmount >= minPayment
    };
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragOver(false);
    
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      const file = files[0];
      if (file.type.startsWith('image/')) {
        setPaymentImage(file);
        const reader = new FileReader();
        reader.onload = (e) => {
          setPaymentImagePreview(e.target.result);
        };
        reader.readAsDataURL(file);
      } else {
        setError('Por favor selecciona solo archivos de imagen');
      }
    }
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      setPaymentImage(file);
      const reader = new FileReader();
      reader.onload = (e) => {
        setPaymentImagePreview(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmitPayment = async () => {
    if (!paymentForm.bank.trim() || !paymentForm.amount.trim()) {
      setError('Banco y valor son obligatorios');
      return;
    }

    setLoading(true);
    try {
      const paymentAmount = parseFloat(paymentForm.amount);
      
      // ✅ VALIDACIÓN: Si sendExcessToBag está marcado, validar que el pago cubra al menos el mínimo
      if (sendExcessToBag && paymentOrder) {
        const excessInfo = calculatePaymentExcess(paymentAmount, paymentOrder);
        const minPayment = parseFloat(paymentOrder.min_payment || 0);
        
        if (paymentAmount < minPayment) {
          setError(`Para enviar excedente a bolsa, el pago debe cubrir al menos el abono mínimo ($${minPayment.toLocaleString('es-CO')})`);
          setLoading(false);
          return;
        }
        
        if (!excessInfo.canSendToBag) {
          setError(`El pago debe ser mayor al mínimo necesario ($${excessInfo.neededForMin.toLocaleString('es-CO')}) para poder enviar excedente a la bolsa`);
          setLoading(false);
          return;
        }
      }
      
      debugLog('🚀 Guardando pago:', {
        orderId: paymentOrder.id,
        bank: paymentForm.bank,
        amount: paymentAmount,
        image: paymentImage ? paymentImage.name : 'Sin imagen',
        sendExcessToBag: sendExcessToBag,
        editingPayment: editingPayment ? editingPayment.id : null
      });
      
      // ✅ INTELIGENCIA: Detectar si es edición o creación
      if (editingPayment) {
        // Es una edición de pago existente
        const updatedPayment = await apiService.payment.update(
          editingPayment.id,
          paymentAmount,
          paymentForm.bank
        );
        debugLog('✅ Pago actualizado exitosamente:', updatedPayment);
      } else {
        // Es un pago nuevo
        const result = await apiService.payment.uploadReceipt(
          paymentOrder.id,                    // orderId
          paymentAmount,                      // amount
          paymentForm.bank,                   // paymentType (usando el campo banco)
          paymentImage,                       // file (puede ser null)
          sendExcessToBag                    // send_excess_to_bag
        );
        debugLog('✅ Pago guardado exitosamente:', result);
        
        // Mostrar notificaciones según el resultado
        if (result.data) {
          const appliedAmount = parseFloat(result.data.amount || 0);
          const excessAmount = parseFloat(result.data.excess_to_advance || 0);
          
          // Verificar si el backend aplicó correctamente
          if (sendExcessToBag) {
            const minPayment = parseFloat(paymentOrder.min_payment || 0);
            const expectedApplied = minPayment;
            
            // El backend debería aplicar solo el mínimo cuando send_excess_to_bag = true
            if (Math.abs(appliedAmount - expectedApplied) > 1) {
              // Hay discrepancia - el backend aplicó más de lo esperado
              errorLog(`⚠️ Discrepancia en aplicación de pago: esperado ${expectedApplied}, aplicado ${appliedAmount}`);
              alert(`✅ Pago registrado exitosamente\n\n⚠️ Nota: Se aplicó $${appliedAmount.toLocaleString('es-CO')} a la orden (esperado mínimo: $${expectedApplied.toLocaleString('es-CO')})\n💼 Excedente enviado a bolsa: $${excessAmount.toLocaleString('es-CO')}\n\n⚠️ El backend aplicó el total de la orden en lugar del mínimo. Por favor, verifica con el administrador.`);
            } else {
              // Todo correcto
              alert(`✅ Pago registrado exitosamente\n\n💰 Monto aplicado a la orden: $${appliedAmount.toLocaleString('es-CO')}\n💼 Excedente enviado a bolsa: $${excessAmount.toLocaleString('es-CO')}`);
            }
          } else if (excessAmount > 0) {
            // No estaba marcado, pero hubo excedente automático
            alert(`✅ Pago registrado exitosamente\n\n💰 Monto aplicado a la orden: $${appliedAmount.toLocaleString('es-CO')}\n💼 Excedente enviado automáticamente a bolsa: $${excessAmount.toLocaleString('es-CO')}`);
          } else {
            alert('✅ Pago registrado exitosamente');
          }
        }
      }
      
      // Cerrar modal y limpiar datos
      setShowPaymentModal(false);
      setShowEditPaymentModal(false);
      setEditingPayment(null);
      setPaymentOrder(null);
      setPaymentForm({ bank: '', amount: '' });
      setPaymentImage(null);
      setPaymentImagePreview(null);
      setSendExcessToBag(false);
      setAdvanceBalance(0);
      setError('');
      
      // Recargar órdenes para actualizar el estado
      await loadOrders();
      
      // Recargar pagos específicamente para esta orden
      try {
        const payments = await apiService.payment.getByOrder(paymentOrder.id);
        setOrderPayments(prev => ({
          ...prev,
          [paymentOrder.id]: payments || []
        }));
      } catch (err) {
        errorLog('Error recargando pagos:', err);
      }
      
    } catch (err) {
      errorLog('❌ Error al guardar pago:', err);
      setError('Error al guardar pago: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  // Función para actualizar un pago existente
  const handleUpdatePayment = async () => {
    if (!paymentForm.bank.trim() || !paymentForm.amount.trim()) {
      setError('Banco y valor son obligatorios');
      return;
    }

    setLoading(true);
    try {
      const updatedPayment = await apiService.payment.update(editingPayment.id, {
        bank: paymentForm.bank,
        amount: parseFloat(paymentForm.amount)
      });

      debugLog('✅ Pago actualizado exitosamente:', updatedPayment);

      // Cerrar modal de edición
      setShowEditPaymentModal(false);
      setEditingPayment(null);
      setPaymentForm({ bank: '', amount: '' });
      setPaymentImage(null);
      setPaymentImagePreview(null);
      setError('');

      // Recargar pagos
      const payments = await apiService.payment.getByOrder(paymentOrder.id);
      setOrderPayments(prev => ({
        ...prev,
        [paymentOrder.id]: payments || []
      }));

      // Recargar órdenes
      await loadOrders();
    } catch (err) {
      errorLog('❌ Error al actualizar pago:', err);
      setError('Error al actualizar pago: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  // Función para manejar el cierre exitoso de en vivo
  const handleEnvivoClosed = async () => {
    // 🔄 RESETEAR TODOS LOS INDICADORES DEL DASHBOARD
    setCurrentEnvivo(null);
    onEnvivoSelect(null); // Limpiar estado global
    setOrders([]);
    setClients([]);
    setProducts([]);
    setKpis({
      totalClients: 0,
      clientsWithOrders: 0,
      totalProducts: 0,
      totalOrders: 0,
      avgOrderValue: 0,
      maxOrder: 0,
      minOrder: 0,
      totalRevenue: 0
    });
    
    // Limpiar estados de modales
    setShowClientModal(false);
    setShowOrderModal(false);
    setShowQuickOrderModal(false);
    
    // Limpiar errores
    setError('');
    
    // Recargar solo los en vivos (sin pedidos/clientes/productos)
    await loadEnvivos(selectedTrip?.id);
  };

  // Función para copiar pedido al portapapeles con formato bonito
  const copyOrderToClipboard = async (order) => {
    try {
      const client = clients.find(c => c.username === order.client_username);
      const total = parseFloat(order.total_amount || 0);
      
      // Obtener información de pagos realizados
      const paymentStatus = getPaymentStatus(order);
      const totalPaid = paymentStatus.totalPaid || 0;
      const remainingBalance = total - totalPaid;
      
      const envivoInfo = currentEnvivo ? `En Vivo ${currentEnvivo.name}` : 'En Vivo';
      const date = new Date().toLocaleDateString('es-CO');
      
      let message = `¡Hola ${client?.username || 'Cliente'}! Gracias por tu compra 🎉\n\n`;
      message += `RESUMEN DE TU PEDIDO - ${envivoInfo} [${date}]\n`;
      message += `━━━━━━━━━━━━━━━━━━\n`;
      
      // Iterar sobre items de la orden con formato limpio
      (order.items || []).forEach((item, idx) => {
        const itemTotal = parseFloat(item.price) * (item.quantity || 1);
        const quantity = item.quantity > 1 ? ` x${item.quantity}` : '';
        message += `${idx + 1}. ${item.productName}${quantity}\n`;
        message += `   💰 $${itemTotal.toLocaleString('es-CO')}\n`;
      });
      
      message += `━━━━━━━━━━━━━━━━━━\n`;
      message += `🛒 TOTAL DEL PEDIDO: $${total.toLocaleString('es-CO')}\n`;
      
      // Mostrar información de pagos de manera clara
      if (paymentStatus.hasPayments) {
        message += `💰 PAGOS REALIZADOS: $${totalPaid.toLocaleString('es-CO')}\n`;
        message += `📊 PORCENTAJE PAGADO: ${paymentStatus.percentage.toFixed(1)}%\n`;
        message += `💳 SALDO PENDIENTE: $${remainingBalance.toLocaleString('es-CO')}\n`;
      } else {
        message += `💰 PAGOS REALIZADOS: $0\n`;
        message += `💳 SALDO PENDIENTE: $${total.toLocaleString('es-CO')}\n`;
      }
      
      message += `━━━━━━━━━━━━━━━━━━\n`;
      
      if (remainingBalance > 0) {
        message += `📌 Envía tu comprobante de pago para confirmar\n`;
        message += `💳 Saldo pendiente: $${remainingBalance.toLocaleString('es-CO')}`;
      } else {
        message += `✅ ¡PEDIDO COMPLETAMENTE PAGADO! 🎉\n`;
        message += `💰 Total pagado: $${totalPaid.toLocaleString('es-CO')}`;
      }

      // Copiar al portapapeles
      await navigator.clipboard.writeText(message);
      
      // Marcar como copiado temporalmente
      setCopiedOrders(prev => new Set([...prev, order.id]));
      
      // Limpiar el estado después de 2 segundos
      setTimeout(() => {
        setCopiedOrders(prev => {
          const newSet = new Set(prev);
          newSet.delete(order.id);
          return newSet;
        });
      }, 2000);
      
      debugLog('✅ Pedido copiado al portapapeles con formato bonito');
      
    } catch (err) {
      errorLog('❌ Error al copiar pedido:', err);
      setError('Error al copiar pedido: ' + err.message);
    }
  };

  // Función para copiar la card como imagen al portapapeles
  const copyOrderAsImage = async (order) => {
    try {
      // Cerrar el menú de copiar antes de capturar
      setCopyMenuOrderId(null);
      
      // Esperar un momento para que el menú se cierre
      await new Promise(resolve => setTimeout(resolve, 100));
      
      // Buscar el elemento de la card por su ID
      const cardElement = document.querySelector(`[data-order-id="${order.id}"]`);
      
      if (!cardElement) {
        throw new Error('No se pudo encontrar la card de la orden');
      }
      
      // Ocultar los elementos que no queremos en la imagen
      const hiddenElements = cardElement.querySelectorAll('.copy-menu-hidden');
      hiddenElements.forEach(el => {
        el.style.display = 'none';
      });
      
      // Esperar un momento adicional para asegurar que todo esté renderizado
      await new Promise(resolve => setTimeout(resolve, 150));
      
      // Importar html2canvas dinámicamente
      const html2canvas = (await import('html2canvas')).default;
      
      // Capturar la card como canvas con opciones mejoradas para evitar cortes de texto
      const canvas = await html2canvas(cardElement, {
        backgroundColor: '#ffffff',
        scale: 3, // Aumentado de 2 a 3 para mejor calidad y evitar cortes
        logging: false,
        useCORS: true,
        allowTaint: false,
        foreignObjectRendering: false, // Mejor renderizado de texto
        removeContainer: false,
        imageTimeout: 15000,
        scrollX: 0,
        scrollY: 0,
        onclone: (clonedDoc) => {
          // Asegurar que los estilos de texto se mantengan en el clon
          const clonedElement = clonedDoc.querySelector(`[data-order-id="${order.id}"]`);
          if (clonedElement) {
            // Forzar que los textos no se corten y asegurar que todo el contenido sea visible
            const textElements = clonedElement.querySelectorAll('*');
            textElements.forEach(el => {
              const computedStyle = clonedDoc.defaultView.getComputedStyle(el);
              if (computedStyle.overflow === 'hidden' || computedStyle.textOverflow === 'ellipsis') {
                el.style.overflow = 'visible';
                el.style.textOverflow = 'clip';
                el.style.whiteSpace = 'normal';
              }
            });
            
            // Asegurar que el elemento no tenga altura máxima que corte contenido
            const clonedStyle = clonedDoc.defaultView.getComputedStyle(clonedElement);
            if (clonedStyle.maxHeight && clonedStyle.maxHeight !== 'none') {
              clonedElement.style.maxHeight = 'none';
            }
            
            // Asegurar que el overflow del elemento padre permita ver todo
            clonedElement.style.overflow = 'visible';
          }
        }
      });
      
      // Convertir canvas a blob
      canvas.toBlob(async (blob) => {
        try {
          // Intentar copiar al portapapeles usando Clipboard API
          await navigator.clipboard.write([
            new ClipboardItem({ 'image/png': blob })
          ]);
          
          debugLog('✅ Imagen copiada al portapapeles');
          
          // Restaurar elementos ocultos
          hiddenElements.forEach(el => {
            el.style.display = '';
          });
          
          // Mostrar mensaje de éxito
          setCopiedOrders(prev => new Set([...prev, order.id]));
          
          // Limpiar el estado después de 2 segundos
          setTimeout(() => {
            setCopiedOrders(prev => {
              const newSet = new Set(prev);
              newSet.delete(order.id);
              return newSet;
            });
          }, 2000);
          
        } catch (err) {
          // Restaurar elementos ocultos
          hiddenElements.forEach(el => {
            el.style.display = '';
          });
          
          // Si falla la Clipboard API, descargar la imagen como fallback
          debugLog('⚠️ No se pudo copiar al portapapeles, descargando imagen:', err);
          
          const url = URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = `pedido-${order.id}.png`;
          link.click();
          URL.revokeObjectURL(url);
          
          alert('La imagen se ha descargado. Algunos navegadores no permiten copiar imágenes al portapapeles.');
        }
      });
      
    } catch (err) {
      // Restaurar elementos ocultos en caso de error
      const cardElement = document.querySelector(`[data-order-id="${order.id}"]`);
      if (cardElement) {
        const hiddenElements = cardElement.querySelectorAll('.copy-menu-hidden');
        hiddenElements.forEach(el => {
          el.style.display = '';
        });
      }
      
      errorLog('❌ Error al copiar imagen:', err);
      setError('Error al copiar imagen: ' + err.message);
    }
  };

  // Función para abrir modal de pedido rápido desde las cards
  const openQuickOrderModal = (client) => {
    if (!client || !client.username) {
      errorLog('Cliente inválido:', client);
      setError('Cliente no válido. Por favor, actualiza la página.');
      return;
    }
    
    setQuickOrderClient(client);
    setQuickOrderForm({
      productName: '',
      price: '',
      clientName: client.username
    });
    setShowQuickOrderModal(true);
  };

  // Función para manejar cambio en el input de cliente del modal rápido
  const handleQuickClientInputChange = (value) => {
    setQuickOrderForm(prev => ({...prev, clientName: value}));
    setQuickOrderClient(null);
    
    const trimmedValue = value.trim();
    
    // ✅ VALIDACIÓN: Solo ejecutar filtro si el texto tiene entre 3 y 8 caracteres
    if (trimmedValue.length >= 3 && trimmedValue.length <= 8) {
      const searchTerm = trimmedValue.toLowerCase();
      
      // ✅ Buscar primero en allClientsCache (todos los clientes)
      const searchInCache = allClientsCache.filter(client => 
        client.username && client.username.toLowerCase().includes(searchTerm)
      );
      
      // ✅ También buscar en clients (clientes del envivo actual)
      const searchInCurrent = clients.filter(client => 
        client.username && client.username.toLowerCase().includes(searchTerm)
      );
      
      // ✅ Combinar resultados y eliminar duplicados por ID
      const combined = [...searchInCache, ...searchInCurrent];
      const unique = Array.from(
        new Map(combined.map(c => [c.id, c])).values()
      );
      
      // ✅ Limitar a 5 sugerencias
      const filtered = unique.slice(0, 5);
      setQuickClientSuggestions(filtered);
      setShowQuickClientSuggestions(filtered.length > 0);
    } else {
      setShowQuickClientSuggestions(false);
      setQuickClientSuggestions([]);
    }
  };

  // Función para seleccionar un cliente del autocompletado
  const selectQuickClient = (client) => {
    setQuickOrderForm(prev => ({...prev, clientName: client.username}));
    setQuickOrderClient(client);
    setShowQuickClientSuggestions(false);
  };

  // Función para manejar cambio en el input de producto del modal rápido
  const handleQuickProductInputChange = (value) => {
    setQuickOrderForm(prev => ({...prev, productName: value}));
    
    const trimmedValue = value.trim();
    
    // ✅ VALIDACIÓN: Solo ejecutar filtro si el texto tiene entre 3 y 8 caracteres
    if (trimmedValue.length >= 3 && trimmedValue.length <= 8) {
      const filtered = products.filter(product => 
        product.name.toLowerCase().includes(trimmedValue.toLowerCase())
      ).slice(0, 5);
      setQuickProductSuggestions(filtered);
      setShowQuickProductSuggestions(filtered.length > 0);
      setSelectedQuickSuggestionIndex(-1);
    } else {
      setShowQuickProductSuggestions(false);
      setQuickProductSuggestions([]);
    }
  };

  // Función para seleccionar sugerencia de producto
  const selectQuickProductSuggestion = (product) => {
    productLog('🎯 Producto seleccionado:', product);
    productLog('💰 Precio del producto (price):', product.price, 'Tipo:', typeof product.price);
    productLog('💰 Precio del producto (base_price):', product.base_price, 'Tipo:', typeof product.base_price);
    productLog('💰 Precio del producto (basePrice):', product.basePrice, 'Tipo:', typeof product.basePrice);
    
    // Usar base_price primero, luego basePrice, luego price como fallback
    const productPrice = product.base_price || product.basePrice || product.price;
    const newPrice = productPrice !== undefined && productPrice !== null ? parseFloat(productPrice).toString() : quickOrderForm.price;
    productLog('💵 Precio calculado para asignar:', newPrice);
    
    setQuickOrderForm(prev => ({
      ...prev, 
      productName: product.name,
      // Auto-completar precio si está disponible (incluye 0 como precio válido)
      price: newPrice
    }));
    setShowQuickProductSuggestions(false);
    setQuickProductSuggestions([]);
    setSelectedQuickSuggestionIndex(-1);
  };

  // Función para manejar teclas en el input de producto
  const handleQuickProductKeyDown = (e) => {
    if (!showQuickProductSuggestions) return;
    
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedQuickSuggestionIndex(prev => 
          prev < quickProductSuggestions.length - 1 ? prev + 1 : 0
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedQuickSuggestionIndex(prev => 
          prev > 0 ? prev - 1 : quickProductSuggestions.length - 1
        );
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedQuickSuggestionIndex >= 0 && selectedQuickSuggestionIndex < quickProductSuggestions.length) {
          selectQuickProductSuggestion(quickProductSuggestions[selectedQuickSuggestionIndex]);
        }
        break;
      case 'Escape':
        setShowQuickProductSuggestions(false);
        setSelectedQuickSuggestionIndex(-1);
        break;
      default:
        // No hacer nada para otras teclas
        break;
    }
  };

  // Funciones para productos rápidos
  const addQuickProduct = () => {
    if (!newProduct.name.trim() || !newProduct.price) {
      setError('Nombre y precio son obligatorios');
      return;
    }

    const product = {
      id: Date.now(), // ID temporal
      name: newProduct.name.trim(),
      price: parseFloat(newProduct.price),
      description: newProduct.description.trim(),
      isNew: true // Marcar como nuevo para distinguir
    };
    
    setQuickProducts(prev => [...prev, product]);
    setNewProduct({ name: '', price: '', description: '' });
    setVoiceText('');
    setError('');
  };

  const removeQuickProduct = (id) => {
    setQuickProducts(prev => prev.filter(p => p.id !== id));
  };

  const saveQuickProducts = async () => {
    if ((quickProducts || []).length === 0) {
      setError('No hay productos para guardar');
      return;
    }
    
    setLoading(true);
    try {
      // Crear todos los productos
      for (const product of quickProducts) {
        productLog('🚀 DEBUG: Creando producto rápido con:', { 
          name: product.name, 
          price: product.price, 
          priceType: typeof product.price,
          description: product.description || '' 
        });
        const result = await apiService.product.create(product.name, product.price, product.description || '', currentEnvivo.id);
        productLog('✅ DEBUG: Resultado de creación:', result);
      }
      
      // Recargar productos
        await loadProducts();
      
      // Limpiar y cerrar modal
      setQuickProducts([]);
      setNewProduct({ name: '', price: '', description: '' });
      setVoiceText('');
      setIsListening(false);
      setShowQuickProductModal(false);
      setError('');
      
      productLog('✅ Productos creados exitosamente');
      
    } catch (err) {
      setError('Error al crear productos: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  // Exportar datos a Excel
  const handleExportExcel = useCallback(async () => {
    if (!currentEnvivo || !currentEnvivo.id) {
      setError('Debes tener un en vivo activo para exportar');
      return;
    }
    
    setLoading(true);
    try {
      // ✅ Obtener datos actuales del envivo para asegurar datos frescos
      const [exportClientsData, exportProductsData, exportOrdersData] = await Promise.all([
        apiService.client.getByEnvivo(currentEnvivo.id),
        apiService.product.getByEnvivo(currentEnvivo.id),
        apiService.order.getByEnvivo(currentEnvivo.id)
      ]);
      
      // ✅ El API devuelve un objeto con la propiedad 'clients', no un array directo
      let currentClients = [];
      if (Array.isArray(exportClientsData)) {
        currentClients = exportClientsData;
      } else if (exportClientsData && exportClientsData.clients && Array.isArray(exportClientsData.clients)) {
        currentClients = exportClientsData.clients;
      }
      
      const currentProducts = Array.isArray(exportProductsData) ? exportProductsData : [];
      const currentOrders = Array.isArray(exportOrdersData) ? exportOrdersData : [];
      
      // Crear workbook de Excel
      const workbook = XLSX.utils.book_new();

      // Hoja 1: Resumen del En Vivo
      const envivoSummary = [
        ['RESUMEN DE EN VIVO'],
        [''],
        ['Nombre de En Vivo:', currentEnvivo.name],
        ['Fecha de Inicio:', currentEnvivo.startTime ? new Date(currentEnvivo.startTime).toLocaleString('es-CO') : 'No registrada'],
        ['Estado:', currentEnvivo.status?.toUpperCase() || 'ACTIVA'],
        ['Viaje:', trips.find(t => t.id === currentEnvivo.tripId)?.name || 'No asignado'],
        [''],
        ['MÉTRICAS GENERALES'],
        ['Total de Clientes:', arrayCounters.clients],
        ['Clientes con Pedidos:', kpis.clientsWithOrders],
        ['Total de Productos:', arrayCounters.products],
        ['Total de Pedidos:', arrayCounters.orders],
        ['Ingresos Totales:', `$${kpis.totalRevenue.toLocaleString('es-CO')}`],
        ['Valor Promedio por Pedido:', `$${kpis.avgOrderValue.toLocaleString('es-CO')}`],
        ['Pedido Mayor:', `$${kpis.maxOrder.toLocaleString('es-CO')}`],
        ['Pedido Menor:', `$${kpis.minOrder.toLocaleString('es-CO')}`]
      ];
      
      const summarySheet = XLSX.utils.aoa_to_sheet(envivoSummary);
      XLSX.utils.book_append_sheet(workbook, summarySheet, 'Resumen');

      // Hoja 2: Clientes
      const clientsData = [
        ['Usuario', 'Nombre', 'Pedidos', 'Total Gastado', 'Estado']
      ];
      
      currentClients.forEach((client) => {
        const clientOrders = currentOrders.filter(order => order.client_username === client.username);
        const totalSpent = (clientOrders || []).reduce((sum, order) => sum + parseFloat(order.total_amount || 0), 0);
        
        clientsData.push([
          `@${client.username}`,
          client.name || '',
          (clientOrders || []).length,
          `$${totalSpent.toLocaleString('es-CO')}`,
          (clientOrders || []).length > 0 ? 'ACTIVO' : 'SIN PEDIDOS'
        ]);
      });
      
      const clientsSheet = XLSX.utils.aoa_to_sheet(clientsData);
      XLSX.utils.book_append_sheet(workbook, clientsSheet, 'Clientes');

      // Hoja 3: Productos
      const productsData = [
        ['Producto', 'Precio Base', 'Total Vendido', 'Unidades Vendidas', 'Ingresos por Producto']
      ];
      
      currentProducts.forEach(product => {
        let totalSold = 0;
        let totalRevenue = 0;
        
        currentOrders.forEach(order => {
          (order.items || []).forEach(item => {
            if (item.productName === product.name) {
              totalSold += item.quantity;
              totalRevenue += item.quantity * parseFloat(item.price);
            }
          });
        });
        
        productsData.push([
          product.name,
          product.base_price || product.basePrice || product.price ? 
            `$${parseFloat(product.base_price || product.basePrice || product.price).toLocaleString('es-CO')}` : 
            '-',
          totalSold,
          totalSold,
          `$${totalRevenue.toLocaleString('es-CO')}`
        ]);
      });
      
      const productsSheet = XLSX.utils.aoa_to_sheet(productsData);
      XLSX.utils.book_append_sheet(workbook, productsSheet, 'Productos');

      // Hoja 4: Pedidos Detallados
      const ordersData = [
        ['ID', 'Cliente', 'Estado', 'Total', 'Productos', 'Fecha']
      ];
      
      currentOrders.forEach(order => {
        const productsList = (order.items || []).map(item => 
          `${item.productName} (x${item.quantity})`
        ).join('; ');
        
        ordersData.push([
          order.id,
          `@${order.client_username}`,
          order.status?.toUpperCase() || 'PENDIENTE',
          `$${parseFloat(order.total_amount).toLocaleString('es-CO')}`,
          productsList,
          order.created_at ? new Date(order.created_at).toLocaleString('es-CO') : 'No registrada'
        ]);
      });
      
      const ordersSheet = XLSX.utils.aoa_to_sheet(ordersData);
      XLSX.utils.book_append_sheet(workbook, ordersSheet, 'Pedidos');

      // Hoja 5: Items de Pedidos
      const itemsData = [
        ['Cliente', 'Producto', 'Cantidad', 'Precio Unitario', 'Subtotal', 'Pedido ID']
      ];
      
      currentOrders.forEach(order => {
        (order.items || []).forEach(item => {
          itemsData.push([
            `@${order.client_username}`,
            item.productName,
            item.quantity,
            `$${parseFloat(item.price).toLocaleString('es-CO')}`,
            `$${(item.quantity * parseFloat(item.price)).toLocaleString('es-CO')}`,
            order.id
          ]);
        });
      });
      
      const itemsSheet = XLSX.utils.aoa_to_sheet(itemsData);
      XLSX.utils.book_append_sheet(workbook, itemsSheet, 'Items');

      // Generar y descargar archivo Excel
      // ✅ Delay de 4 segundos para mostrar la animación de loading
      await new Promise(resolve => setTimeout(resolve, 4000));
      
      const fileName = `makana-envivo-${currentEnvivo.name.replace(/[^a-zA-Z0-9]/g, '-')}-${new Date().toISOString().split('T')[0]}.xlsx`;
      XLSX.writeFile(workbook, fileName);

      setError('');
      
    } catch (err) {
      setError('Error al exportar Excel: ' + err.message);
    } finally {
      setLoading(false);
    }
  }, [currentEnvivo, kpis, arrayCounters, trips]); // ✅ CON DEPENDENCIAS PARA ACCEDER A VALORES ACTUALES

  // Escuchar eventos de acciones rápidas desde el menú móvil
  useEffect(() => {
    const handleQuickAction = (event) => {
      const action = event.detail;
      
      switch (action) {
        case 'refresh':
          loadAllData();
          break;
        case 'newEnvivo':
          createEnvivo();
          break;
        case 'addClients':
          setShowClientModal(true);
          break;
        case 'quickOrder':
          if ((clients || []).length === 0) {
            setError('Primero debes agregar clientes al en vivo');
            return;
          }
          setQuickOrderClient(null);
          setQuickOrderForm({ productName: '', price: '', clientName: '' });
          setShowQuickOrderModal(true);
          break;
        case 'quickProduct':
          setShowQuickProductModal(true);
          break;
        case 'manualOrder':
          if ((clients || []).length === 0) {
            setError('Primero debes agregar clientes al en vivo');
            return;
          }
          setOrderForm({ clientId: '', products: [] });
          setShowOrderModal(true);
          break;
        case 'exportExcel':
          handleExportExcel();
          break;
        default:
          debugLog('Acción no reconocida:', action);
      }
    };

    window.addEventListener('quickAction', handleQuickAction);
    
    return () => {
      window.removeEventListener('quickAction', handleQuickAction);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // ✅ SIN DEPENDENCIAS PARA EVITAR RE-RENDERS MASIVOS

  return (
    <>
      {/* Loading Overlay para exportación de Excel */}
      {loading && (
        <div className="fixed inset-0 bg-black bg-opacity-80 flex items-center justify-center z-50" style={{ fontFamily: 'Arial, sans-serif' }}>
          <div className="flex flex-col items-center gap-6">
            {/* Spinner circular */}
            <div className="relative" style={{ width: '120px', height: '120px' }}>
              <svg className="animate-spin" style={{ width: '120px', height: '120px' }} viewBox="0 0 100 100">
                <circle
                  cx="50"
                  cy="50"
                  r="45"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="8"
                  strokeDasharray="70 208"
                  strokeDashoffset="0"
                  className="text-cyan-400"
                />
              </svg>
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="text-cyan-400 text-2xl font-bold">⇩</div>
              </div>
            </div>
            
            {/* Barra de progreso */}
            <div className="w-80 h-4 border-2 border-cyan-400 rounded overflow-hidden">
              <div className="h-full bg-gradient-to-r from-cyan-400 to-cyan-600 animate-pulse" style={{ width: '70%' }}></div>
            </div>
            
            {/* Texto */}
            <div className="text-cyan-400 text-2xl font-bold tracking-wider">
              LOADING ...
            </div>
            
            <div className="text-gray-300 text-sm mt-2">
              Generando archivo Excel...
            </div>
          </div>
        </div>
      )}
      
      {/* Barra de Acciones con sticky behavior - Oculto en móvil */}
      <div className="w-full px-2 sm:px-4 md:px-6 lg:px-8 xl:px-12">
        <div 
          ref={secondMenuRef}
          className="hidden md:block bg-white rounded-2xl shadow-lg p-2 mb-6 sticky transition-all duration-200"
          style={{
            top: '65px', // Altura real del primer menú medida: 65px
            marginTop: isSecondMenuSticky ? '0px' : '1rem',
            position: 'sticky',
            zIndex: 49, // Justo debajo del primer menú (z-50) pero visible
            willChange: 'transform'
          }}
        >
          <div className="flex flex-wrap items-center justify-center gap-1">
            <button
              onClick={loadAllData}
              className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-gray-700 hover:text-green-600 hover:bg-green-50 rounded-lg transition-colors whitespace-nowrap"
            >
              <RefreshCw size={14} />
              Actualizar
            </button>
            
            <button
              onClick={createEnvivo}
              className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-gray-700 hover:text-purple-600 hover:bg-purple-50 rounded-lg transition-colors whitespace-nowrap"
            >
              <Plus size={14} />
              Nuevo En Vivo
            </button>
            
            <button
              onClick={() => setShowClientModal(true)}
              className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-gray-700 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors whitespace-nowrap"
            >
              <Users size={14} />
              Agregar Clientes
            </button>
            
            <button
              onClick={() => {
                if (arrayCounters.clients === 0) {
                  setError('Primero debes agregar clientes al en vivo');
                  return;
                }
                setQuickOrderClient(null); // Permitir selección de cliente
                setQuickOrderForm({ productName: '', price: '', clientName: '' });
                setShowQuickOrderModal(true);
              }}
              className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-gray-700 hover:text-green-600 hover:bg-green-50 rounded-lg transition-colors whitespace-nowrap"
            >
              <ShoppingBag size={14} />
              Pedido Rápido
            </button>
            
            <button
              onClick={() => setShowQuickProductModal(true)}
              className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-gray-700 hover:text-purple-600 hover:bg-purple-50 rounded-lg transition-colors whitespace-nowrap"
            >
              <Package size={14} />
              Producto Rápido
            </button>

            <button
              onClick={() => setShowOrderModal(true)}
              className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-gray-700 hover:text-orange-600 hover:bg-orange-50 rounded-lg transition-colors whitespace-nowrap"
            >
              <Package size={14} />
              Pedido Manual
            </button>
            
            <button
              onClick={handleExportExcel}
              className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-gray-700 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors whitespace-nowrap"
            >
              <Download size={14} />
              Exportar Excel
            </button>
            
            {currentEnvivo && (
              <button
                onClick={closeEnvivo}
                className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-gray-700 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors whitespace-nowrap"
              >
                <X size={14} />
                Cerrar En Vivo
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="w-full px-2 sm:px-4 md:px-6 lg:px-8 xl:px-12">
        {/* Layout de 3 columnas compacto */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
          {/* Selector de Viaje */}
          <div className="bg-white rounded-2xl shadow-lg p-6">
            <div className="flex items-center gap-4 mb-4">
              <MapPin className="text-blue-600" size={24} />
              <div>
                <h2 className="text-lg font-semibold text-gray-800">Viaje Seleccionado</h2>
                <p className="text-sm text-gray-600">Selecciona el viaje para crear en vivos</p>
            </div>
          </div>
            
            <TripSelector
              selectedTripId={selectedTrip?.id}
              onTripSelect={onTripSelect}
              placeholder="Selecciona un viaje para comenzar"
              required={true}
            />
            
            {selectedTrip && (
              <div className="mt-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <p className="font-medium text-blue-900 text-lg">{selectedTrip.name?.replace(/-$/, '')}</p>
                    {selectedTrip.startDate && selectedTrip.endDate && (
                      <p className="text-sm text-blue-700">
                        {new Date(selectedTrip.startDate).toLocaleDateString('es-CO')} - {new Date(selectedTrip.endDate).toLocaleDateString('es-CO')}
                      </p>
                    )}
                  </div>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                    selectedTrip.status === 'planning' ? 'bg-yellow-100 text-yellow-800' :
                    selectedTrip.status === 'active' ? 'bg-green-100 text-green-800' :
                    'bg-blue-100 text-blue-800'
                  }`}>
                    {selectedTrip.status === 'planning' ? 'Planificando' :
                     selectedTrip.status === 'active' ? 'Activo' : 'Completado'}
                  </span>
                </div>
                
                {/* Indicadores financieros del viaje */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-3 pt-3 border-t border-blue-200">
                  <div className="bg-white rounded-lg p-2 shadow-sm flex flex-col justify-between h-16">
                    <p className="text-xs text-gray-600">Total Vendido</p>
                    <p className="text-sm font-semibold text-blue-600">
                      ${selectedTrip.total_revenue ? parseFloat(selectedTrip.total_revenue).toLocaleString('es-CO') : '0'}
                    </p>
                  </div>
                  
                  <div className="bg-white rounded-lg p-2 shadow-sm flex flex-col justify-between h-16">
                    <p className="text-xs text-gray-600">Total Abonado</p>
                    <p className="text-sm font-semibold text-green-600">
                      ${selectedTrip.total_paid ? parseFloat(selectedTrip.total_paid).toLocaleString('es-CO') : '0'}
                    </p>
                  </div>
                  
                  <div className="bg-white rounded-lg p-2 shadow-sm flex flex-col justify-between h-16">
                    <p className="text-xs text-gray-600">Saldo Pendiente</p>
                    <p className="text-sm font-semibold text-orange-600">
                      ${selectedTrip.remaining_balance ? parseFloat(selectedTrip.remaining_balance).toLocaleString('es-CO') : '0'}
                    </p>
                  </div>
                  
                  <div className="bg-white rounded-lg p-2 shadow-sm flex flex-col justify-between h-16">
                    <p className="text-xs text-gray-600">Abonos</p>
                    <p className="text-sm font-semibold text-purple-600">
                      {selectedTrip.total_payments || 0}
                    </p>
                  </div>
                </div>
              </div>
            )}
                </div>

          {/* Lista de En Vivos */}
          <div className="bg-white rounded-2xl shadow-lg p-6">
            <h2 className="text-xl font-semibold text-gray-800 mb-4 flex items-center gap-2">
              <Calendar className="text-purple-600" size={24} />
              En Vivos
            </h2>
            
            <div className="space-y-3">
                      {(envivos || [])
                        .filter(envivo => {
                          // ✅ FILTRO ADICIONAL: Solo mostrar envivos del viaje seleccionado
                          if (!selectedTrip) return true; // Si no hay viaje seleccionado, mostrar todos
                          return envivo.trip_id === selectedTrip.id || envivo.tripId === selectedTrip.id;
                        })
                        .map((envivo) => {
                        // Debug log para en vivo específico
                        if (envivo.name.includes('Tommy')) {
                          debugLog('🔍 DEBUG: En Vivo Tommy encontrado:', envivo);
                          debugLog('🔍 DEBUG: envivo.payments:', envivo.payments);
                          debugLog('🔍 DEBUG: envivo.payments?.length:', envivo.payments?.length);
                        }
                        
                        return (
                          <div
                            key={envivo.id}
                  className={`p-4 border rounded-lg cursor-pointer transition-colors ${
                      currentEnvivo?.id === envivo.id
                      ? 'border-purple-300 bg-purple-50'
                      : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                  }`}
                    onClick={() => {
                      setCurrentEnvivo(envivo);
                      onEnvivoSelect(envivo);
                    }}
                        >
                          <div className="flex items-center justify-between">
                    <div>
                      <h3 className="font-medium text-gray-800">{envivo.name}</h3>
                      <p className="text-sm text-gray-600">
                        {envivo.startTime && new Date(envivo.startTime).toLocaleString('es-CO')}
                      </p>
                              </div>
                    <div className="space-y-2">
                      {/* Botones de estado y reabrir */}
                    <div className="flex items-center gap-2">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                  envivo.status === 'active'
                                  ? 'bg-green-100 text-green-800'
                          : 'bg-gray-100 text-gray-800'
                              }`}>
                                  {envivo.status === 'active' ? 'ACTIVA' : 'CERRADA'}
                              </span>
                                {envivo.status !== 'active' && (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                      reopenEnvivo(envivo);
                                  }}
                                  className="text-xs bg-purple-600 hover:bg-purple-700 text-white px-2 py-1 rounded-full transition-colors whitespace-nowrap"
                                >
                                  Reabrir
                                </button>
                              )}
                            </div>
                      
                      {/* Información de medios de pago para en vivos cerrados */}
                      {envivo.status !== 'active' && (
                        <div className="space-y-1">
                          {/* Nombres de tarjetas debajo de los botones */}
                          {envivo.payments && (envivo.payments || []).length > 0 ? (
                            <div className="text-[0.45rem] text-gray-500 space-y-0.5">
                              {(envivo.payments || []).map((payment, index) => (
                                <div key={index} className="truncate">
                                  {payment.cardName}
                          </div>
                              ))}
                            </div>
                          ) : (
                            <div className="text-[0.45rem] text-red-500 bg-red-50 px-1 py-0.5 rounded border border-red-200">
                              Sin medio de pago reportado
                            </div>
                          )}
                          
                          {/* Montos por tarjeta */}
                          {envivo.payments && (envivo.payments || []).length > 0 && (
                            <div className="text-[0.45rem] text-green-600 space-y-0.5">
                              {(envivo.payments || []).map((payment, index) => (
                                <div key={index} className="truncate">
                                  {payment.amountUsd && `$${parseFloat(payment.amountUsd).toFixed(2)} USD`}
                                  {payment.amountUsd && payment.amountCop && ' + '}
                                  {payment.amountCop && `$${parseFloat(payment.amountCop).toLocaleString('es-CO')} COP`}
                        </div>
                      ))}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                          </div>
                        </div>
                        );
                      })}
                    
              {arrayCounters.envivos === 0 && (
                <div className="text-center py-8 text-gray-500">
                  <Calendar size={32} className="mx-auto mb-2" />
                  <p>No hay en vivos creados</p>
                  <p className="text-sm">Crea un nuevo en vivo para comenzar</p>
                      </div>
                    )}
                  </div>
                </div>

          {/* Tercera columna: En Vivo Activo */}
          <div className="bg-white rounded-2xl shadow-lg p-6">
            <h2 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
              <Calendar className="text-green-600" size={20} />
              En Vivo Activo
            </h2>
            
            {currentEnvivo ? (
              <div className="space-y-4">
                <div className="p-3 bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-lg">
                  <div className="mb-3">
                    <span className="font-medium text-green-800 text-sm leading-tight block">{currentEnvivo.name}</span>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      currentEnvivo.status === 'active' 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-red-100 text-red-800'
                    }`}>
                      {currentEnvivo.status === 'active' ? 'ACTIVA' : 'CERRADA'}
                    </span>
            </div>
                  
              </div>
              
                <button
                  onClick={closeEnvivo}
                  className="w-full bg-red-500 hover:bg-red-600 text-white px-3 py-2 rounded-lg text-sm font-medium transition-colors"
                >
                  Cerrar En Vivo
                </button>
                
                {currentEnvivo.status !== 'active' && (
                  <button
                    onClick={() => reopenEnvivo(currentEnvivo)}
                    className="w-full bg-purple-600 hover:bg-purple-700 text-white px-3 py-2 rounded-lg text-sm font-medium transition-colors"
                  >
                    Reabrir En Vivo
                  </button>
                )}
                </div>
            ) : (
              <div className="text-center py-6 text-gray-500">
                <Calendar size={24} className="mx-auto mb-2" />
                <p className="text-sm">No hay en vivo activo</p>
                <p className="text-xs">Selecciona un en vivo para ver detalles</p>
              </div>
            )}
            </div>
          </div>
          
          {/* Sección de en vivo actual eliminada - ahora está en la tercera columna */}

        {/* KPIs Mejorados */}
      <div className="bg-white rounded-2xl shadow-lg p-4 md:p-6 mb-6">
        <h2 className="text-base md:text-lg lg:text-xl font-semibold text-gray-800 mb-3 md:mb-4 flex items-center gap-2 flex-wrap">
          <TrendingUp className="text-green-600" size={18} />
          📊 Indicadores de Rendimiento
          {currentEnvivo && (
            <span className="text-xs md:text-sm text-gray-500 font-normal">
              - {currentEnvivo.name}
            </span>
          )}
        </h2>
        
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 md:gap-4">
          {/* Clientes */}
          <div className="bg-gradient-to-r from-blue-50 to-blue-100 p-3 md:p-4 rounded-lg border border-blue-200">
            <div className="flex items-center justify-between mb-1.5 md:mb-2">
              <div className="flex items-center gap-1.5 md:gap-2">
                <Users className="text-blue-600" size={16} />
                <span className="text-xs md:text-sm font-medium text-blue-800">Clientes</span>
              </div>
              <span className="text-[0.65rem] md:text-xs bg-blue-200 text-blue-800 px-1.5 py-0.5 md:px-2 md:py-1 rounded-full whitespace-nowrap">
                {kpis.totalClients > 0 ? Math.round((kpis.clientsWithOrders / kpis.totalClients) * 100) : 0}% activos
              </span>
            </div>
            <div className="text-lg md:text-xl lg:text-2xl font-bold text-blue-900">{kpis.totalClients}</div>
            <div className="text-[0.65rem] md:text-xs text-blue-700 mt-1 leading-tight">
              {kpis.clientsWithOrders} con pedidos ({kpis.totalClients - kpis.clientsWithOrders} sin pedidos)
          </div>
        </div>

          {/* Productos */}
          <div className="bg-gradient-to-r from-purple-50 to-purple-100 p-3 md:p-4 rounded-lg border border-purple-200">
            <div className="flex items-center gap-1.5 md:gap-2 mb-1.5 md:mb-2">
              <Package className="text-purple-600" size={16} />
              <span className="text-xs md:text-sm font-medium text-purple-800">Productos</span>
            </div>
            <div className="text-lg md:text-xl lg:text-2xl font-bold text-purple-900">{kpis.totalProducts}</div>
            <div className="text-[0.65rem] md:text-xs text-purple-700 mt-1 leading-tight">
              {(products || []).filter(p => (orders || []).some(o => o.items?.some(i => i.productName === p.name))).length} vendidos
            </div>
          </div>
          
          {/* Pedidos */}
          <div className="bg-gradient-to-r from-green-50 to-green-100 p-3 md:p-4 rounded-lg border border-green-200">
            <div className="flex items-center gap-1.5 md:gap-2 mb-1.5 md:mb-2">
              <ShoppingBag className="text-green-600" size={16} />
              <span className="text-xs md:text-sm font-medium text-green-800">Pedidos</span>
              </div>
            <div className="text-lg md:text-xl lg:text-2xl font-bold text-green-900">{kpis.totalOrders}</div>
            <div className="text-[0.65rem] md:text-xs text-green-700 mt-1 leading-tight">
              {(orders || []).filter(o => o.status === 'pending').length} pendientes, {(orders || []).filter(o => o.status === 'completed').length} completados
            </div>
          </div>
          
          {/* Ingresos */}
          <div className="bg-gradient-to-r from-orange-50 to-orange-100 p-3 md:p-4 rounded-lg border border-orange-200">
            <div className="flex items-center justify-between mb-1.5 md:mb-2">
            <div className="flex items-center gap-1.5 md:gap-2">
                <DollarSign className="text-orange-600" size={16} />
                <span className="text-xs md:text-sm font-medium text-orange-800">Ingresos</span>
              </div>
              <span className="text-[0.65rem] md:text-xs bg-orange-200 text-orange-800 px-1.5 py-0.5 md:px-2 md:py-1 rounded-full whitespace-nowrap">
                ${kpis.avgOrderValue.toLocaleString('es-CO')} prom
              </span>
            </div>
            <div className="text-lg md:text-xl lg:text-2xl font-bold text-orange-900 break-words">
              ${kpis.totalRevenue.toLocaleString('es-CO')}
          </div>
            <div className="text-[0.65rem] md:text-xs text-orange-700 mt-1 leading-tight">
              Pedido mayor: ${kpis.maxOrder.toLocaleString('es-CO')} | Menor: ${kpis.minOrder.toLocaleString('es-CO')}
            </div>
            
            {/* Información de medios de pago */}
            <div className="mt-2 text-[0.5rem] text-orange-600 space-y-0.5">
              {(() => {
                // Recopilar todos los pagos de en vivos cerrados
                const closedEnvivos = (envivos || []).filter(e => e.status !== 'active' && e.payments && (e.payments || []).length > 0);
                if (closedEnvivos.length === 0) {
                  return (
                    <div className="text-red-500 bg-red-50 px-1 py-0.5 rounded border border-red-200">
                      Sin medios de pago reportados
                    </div>
                  );
                }
                
                // Agrupar pagos por tarjeta
                const paymentSummary = {};
                closedEnvivos.forEach(envivo => {
                  envivo.payments.forEach(payment => {
                    if (!paymentSummary[payment.cardName]) {
                      paymentSummary[payment.cardName] = { usd: 0, cop: 0 };
                    }
                    if (payment.amountUsd) paymentSummary[payment.cardName].usd += parseFloat(payment.amountUsd);
                    if (payment.amountCop) paymentSummary[payment.cardName].cop += parseFloat(payment.amountCop);
                  });
                });
                
                return Object.entries(paymentSummary).map(([cardName, amounts], index) => (
                  <div key={index} className="truncate">
                    {cardName}: {amounts.usd > 0 && `$${amounts.usd.toFixed(2)} USD`}
                    {amounts.usd > 0 && amounts.cop > 0 && ' + '}
                    {amounts.cop > 0 && `$${amounts.cop.toLocaleString('es-CO')} COP`}
                  </div>
                ));
              })()}
            </div>
          </div>

          {/* Abonos / Pagos - Estadísticas de clientes con pedidos vs abonos */}
          {(() => {
            const clientsWithAbonoSet = new Set();
            const clientsWithAbono50Set = new Set();
            (orders || []).forEach(order => {
              const status = getPaymentStatus(order);
              const clientKey = order.client_username || order.client_id;
              if (!clientKey) return;
              if (status.totalPaid > 0) clientsWithAbonoSet.add(clientKey);
              if (status.isMinPaid) clientsWithAbono50Set.add(clientKey);
            });
            const clientsWithAbono = clientsWithAbonoSet.size;
            const clientsWithAbono50 = clientsWithAbono50Set.size;
            const clientsWithOrdersCount = kpis.clientsWithOrders || 0;
            const clientsWith0Abono = clientsWithOrdersCount - clientsWithAbono;
            const clientsWithAbonoBelow50 = clientsWithAbono - clientsWithAbono50;
            const pctConAbono = clientsWithOrdersCount > 0 ? Math.round((clientsWithAbono / clientsWithOrdersCount) * 100) : 0;
            const pctAbono50 = clientsWithOrdersCount > 0 ? Math.round((clientsWithAbono50 / clientsWithOrdersCount) * 100) : 0;
            const pct0Abono = clientsWithOrdersCount > 0 ? Math.round((clientsWith0Abono / clientsWithOrdersCount) * 100) : 0;
            const pctAbonoBelow50 = clientsWithOrdersCount > 0 ? Math.round((clientsWithAbonoBelow50 / clientsWithOrdersCount) * 100) : 0;
            return (
              <div className="bg-gradient-to-r from-teal-50 to-teal-100 p-3 md:p-4 rounded-lg border border-teal-200">
                <div className="flex items-center gap-1.5 md:gap-2 mb-1.5 md:mb-2">
                  <Wallet className="text-teal-600" size={16} />
                  <span className="text-xs md:text-sm font-medium text-teal-800">Abonos</span>
                </div>
                <div className="text-lg md:text-xl lg:text-2xl font-bold text-teal-900">
                  {clientsWithOrdersCount} con pedido
                </div>
                <div className="text-[0.65rem] md:text-xs text-teal-700 mt-1 space-y-1 leading-tight">
                  <div className="flex items-center gap-2 text-red-600">
                    <span>{clientsWith0Abono} con $0 abono</span>
                    <span className="font-semibold">({pct0Abono}%)</span>
                  </div>
                  <div className="flex items-center gap-2 text-red-600">
                    <span>{clientsWithAbonoBelow50} abono &lt;50%</span>
                    <span className="font-semibold">({pctAbonoBelow50}%)</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span>{clientsWithAbono} con abono</span>
                    <span className="font-semibold text-teal-800">({pctConAbono}%)</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span>{clientsWithAbono50} abono 50%+</span>
                    <span className="font-semibold text-teal-800">({pctAbono50}%)</span>
                  </div>
                </div>
              </div>
            );
          })()}
        </div>
        
        {/* Resumen adicional */}
        {currentEnvivo && kpis.totalClients > 0 && (
          <div className="mt-4 p-2 md:p-3 bg-gray-50 rounded-lg">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-4 text-xs md:text-sm">
              <div className="flex items-center gap-2">
                <span className="text-gray-600">Tasa de conversión:</span>
                <span className="font-semibold text-green-600">
                  {kpis.totalClients > 0 ? Math.round((kpis.clientsWithOrders / kpis.totalClients) * 100) : 0}%
                </span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-gray-600">Productos por pedido:</span>
                <span className="font-semibold text-blue-600">
                  {kpis.totalOrders > 0 ? Math.round((orders || []).reduce((total, order) => total + (order.items?.length || 0), 0) / kpis.totalOrders * 10) / 10 : 0}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-gray-600">Valor promedio por cliente:</span>
                <span className="font-semibold text-purple-600">
                  ${kpis.totalClients > 0 ? Math.round(kpis.totalRevenue / kpis.totalClients).toLocaleString('es-CO') : 0}
                </span>
              </div>
            </div>
          </div>
        )}
              </div>

      {/* Cards de Órdenes por Cliente */}
      {currentEnvivo && arrayCounters.orders > 0 && (
        <div className="bg-white rounded-2xl shadow-lg p-6 mb-6">
          <h2 className="text-xl font-semibold text-gray-800 mb-4 flex items-center gap-2">
            <ShoppingBag className="text-blue-600" size={24} />
            📦 Órdenes por Cliente
          </h2>
          
          <div className="orders-masonry-container">
            {(orders || []).map((order) => {
              // ✅ USAR client_id en lugar de username para búsqueda más confiable
              const client = (clients || []).find(c => c.id === order.client_id);
              
              return (
                <div 
                  key={order.id}
                  data-order-id={order.id}
                  className={`border rounded-lg p-4 hover:shadow-md transition-shadow cursor-pointer ${getCardColorByLogisticsStatus(order.delivery_status || '')}`}
                  onClick={() => {
                    debugLog('🔍 DEBUG CARD CLICK:', {
                      orderId: order.id,
                      clientId: order.client_id,
                      clientUsername: order.client_username,
                      totalClients: (clients || []).length,
                      clientFound: !!client,
                      firstClient: (clients || [])[0]
                    });
                    
                    if (client) {
                      openQuickOrderModal(client);
                    } else {
                      // ✅ SOLUCIÓN TEMPORAL: Crear cliente temporal con datos de la orden
                      debugLog('⚠️ Creando cliente temporal para la orden:', order.client_username);
                      const tempClient = {
                        id: order.client_id,
                        username: order.client_username,
                        name: order.client_username // Usar username como nombre temporal
                      };
                      openQuickOrderModal(tempClient);
                    }
                  }}
                  title="Click para agregar otro pedido a este cliente"
                >
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center text-white text-base font-bold">
                        {order.client_id || '?'}
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-800">@{order.client_username}</h3>
                        {client?.name && (
                          <p className="text-xs text-gray-600">{client.name}</p>
                        )}
                      </div>
                    </div>
                    <span className={`px-2 py-1 rounded-full text-[0.5rem] font-medium ${
                      order.status === 'pending' 
                        ? 'bg-yellow-100 text-yellow-800' 
                        : order.status === 'completed'
                        ? 'bg-green-100 text-green-800'
                        : 'bg-gray-100 text-gray-800'
                    }`}>
                      {order.status === 'pending' ? 'PENDIENTE' : order.status === 'completed' ? 'COMPLETADO' : order.status?.toUpperCase()}
                    </span>
              </div>
                  
                  <div className="space-y-2 mb-3">
                    {((order?.items || []) || []).map((item, index) => {
                      const isCarryOn = item.carry_on === 1;
                      const itemId = item.id; // ID del item para la API
                      const isUpdating = carryOnUpdatingId === itemId;
                      
                      return (
                      <div key={index} className="flex items-start justify-between text-sm bg-white rounded px-2 py-1 group">
                        <div className="flex items-start justify-between w-full gap-2">
                          <div className="flex items-start gap-2 flex-1 min-w-0" style={{ maxWidth: '60%' }}>
                            {/* Checkbox para carry_on */}
                            <input
                              type="checkbox"
                              checked={isCarryOn}
                              onChange={(e) => {
                                e.stopPropagation();
                                if (itemId) {
                                  handleToggleCarryOn(itemId, isCarryOn);
                                } else {
                                  errorLog('❌ Item ID no encontrado, no se puede actualizar carry_on');
                                }
                              }}
                              disabled={isUpdating || !itemId}
                              className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0 mt-0.5"
                              title={isCarryOn ? "Marcado: Viaja en maleta (100% abono)" : "Sin marcar: Normal (50% abono)"}
                              onClick={(e) => e.stopPropagation()}
                            />
                            <span 
                              className="text-gray-700 text-left" 
                              title={item.productName || ''}
                              style={{ 
                                wordBreak: 'break-word', 
                                lineHeight: '1.3',
                                overflow: 'visible',
                                whiteSpace: 'normal',
                                display: 'block'
                              }}
                            >
                              {item.productName || ''}
                            </span>
                          </div>
                          <div className="flex items-center gap-3 text-right flex-shrink-0">
                            <span className="text-gray-500 font-medium whitespace-nowrap">x{item.quantity || 1}</span>
                            <span className="text-blue-600 font-medium whitespace-nowrap">${parseFloat(item.price || 0).toLocaleString('es-CO')}</span>
                            {isCarryOn && (
                              <span className="text-xs text-green-600 font-medium flex-shrink-0" title="Este producto viaja en maleta">
                                ✈️
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleEditItemInOrder(order.id, index, item);
                            }}
                            className="p-1 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded transition-colors"
                            title="Editar producto"
                          >
                            <Edit2 size={12} />
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleRemoveItemFromOrder(order.id, index);
                            }}
                            className="p-1 text-red-600 hover:text-red-800 hover:bg-red-50 rounded transition-colors"
                            title="Eliminar producto"
                          >
                            <Trash2 size={12} />
                          </button>
                        </div>
                      </div>
                      );
                    })}
            </div>
                  
                  <div className="border-t border-blue-200 pt-3 mt-3">
                    {/* ✅ NUEVO: Información detallada de pagos con esquema de colores */}
                    <div className="space-y-2 mb-3">
                      {/* Total de la orden */}
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium text-gray-700">💰 Total:</span>
                        <span className="text-lg font-bold text-blue-600">
                          ${parseFloat(order.total_amount).toLocaleString('es-CO')}
                        </span>
                      </div>
                      
                      {/* Abono mínimo (solo si hay items en maleta) */}
                      {(() => {
                        const paymentStatus = getPaymentStatus(order);
                        const carryOnExtra = order.carry_on_extra !== undefined ? parseFloat(order.carry_on_extra || 0) : calculateCarryOnExtra(order.items || []);
                        if (carryOnExtra > 0) {
                          return (
                            <div className="flex items-center justify-between">
                              <span className="text-sm font-medium text-gray-700">✈️ Abono Mín. (con maleta):</span>
                              <span className="text-xs font-semibold text-purple-600">
                                ${paymentStatus.minPayment.toLocaleString('es-CO')}
                              </span>
                            </div>
                          );
                        }
                        return null;
                      })()}
                      
                      {/* Total pagado */}
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium text-gray-700">✅ Abonado:</span>
                        <span className="text-sm font-semibold text-green-600">
                          ${(() => {
                            const paymentStatus = getPaymentStatus(order);
                            return paymentStatus.totalPaid.toLocaleString('es-CO');
                          })()}
                        </span>
                      </div>
                      
                      {/* Saldo por pagar */}
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium text-gray-700">⏳ Saldo:</span>
                        <span className={`text-sm font-semibold ${
                          (() => {
                            const paymentStatus = getPaymentStatus(order);
                            const remaining = paymentStatus.remainingAmount;
                            if (remaining <= 0) {
                              return 'text-green-600'; // Completamente pagado
                            } else {
                              // Usar min_payment en lugar de 50% para determinar el color
                              const minPayment = paymentStatus.minPayment;
                              if (remaining < minPayment) {
                                return 'text-yellow-600'; // Más del abono mínimo pagado
                              } else {
                                return 'text-red-600'; // Menos del abono mínimo pagado
                              }
                            }
                          })()
                        }`}>
                          ${(() => {
                            const paymentStatus = getPaymentStatus(order);
                            return paymentStatus.remainingAmount.toLocaleString('es-CO');
                          })()}
                        </span>
                      </div>
                      
                      {/* Barra de progreso visual */}
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div 
                          className={`h-2 rounded-full transition-all duration-300 ${
                            (() => {
                              const paymentStatus = getPaymentStatus(order);
                              if (paymentStatus.percentage >= 100) {
                                return 'bg-green-500'; // Completamente pagado
                              } else if (paymentStatus.isMinPaid) {
                                return 'bg-yellow-500'; // Más del abono mínimo pagado
                              } else {
                                return 'bg-red-500'; // Menos del abono mínimo pagado
                              }
                            })()
                          }`}
                          style={{ 
                            width: `${(() => {
                              const paymentStatus = getPaymentStatus(order);
                              return Math.min(paymentStatus.percentage, 100);
                            })()}%` 
                          }}
                        ></div>
                      </div>
                      
                      {/* Porcentaje pagado */}
                      <div className="flex items-center justify-center">
                        <span className="text-xs font-medium text-gray-600">
                          {(() => {
                            const paymentStatus = getPaymentStatus(order);
                            return `${paymentStatus.percentage.toFixed(1)}% pagado`;
                          })()}
                        </span>
                      </div>
                    </div>
                    
                    <div className="flex items-center justify-between mt-auto">
                      <div className="text-xs text-gray-500">
                        {order.items?.length || 0} producto{(order.items?.length || 0) !== 1 ? 's' : ''}
                      </div>
                      <div className="flex items-center gap-2 copy-menu-hidden">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleLoadPayment(order);
                          }}
                          className={`flex items-center gap-1 px-2 py-1 text-xs font-medium rounded transition-colors ${
                            (() => {
                              const paymentStatus = getPaymentStatus(order);
                              if (paymentStatus.isMinPaid) {
                                return 'text-green-700 bg-green-100 hover:text-green-800 hover:bg-green-200';
                              } else {
                                return 'text-red-700 bg-red-100 hover:text-red-800 hover:bg-red-200';
                              }
                            })()
                          }`}
                          title={`Cargar pago - ${(() => {
                            const paymentStatus = getPaymentStatus(order);
                            return paymentStatus.hasPayments 
                              ? `${paymentStatus.percentage.toFixed(1)}% pagado ($${paymentStatus.totalPaid.toLocaleString('es-CO')})`
                              : 'Sin pagos registrados';
                          })()}`}
                        >
                          <DollarSign size={12} />
                          <span>
                            ${(() => {
                              const paymentStatus = getPaymentStatus(order);
                              return paymentStatus.hasPayments 
                                ? `${paymentStatus.totalPaid.toLocaleString('es-CO')} Pago`
                                : 'Pago';
                            })()}
                          </span>
                        </button>
                        <div className="relative copy-menu-container">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setCopyMenuOrderId(copyMenuOrderId === order.id ? null : order.id);
                            }}
                            className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
                            title="Copiar pedido"
                          >
                            {copiedOrders.has(order.id) ? (
                              <>
                                <Check size={12} className="text-green-600" />
                                <span className="text-green-600">Copiado!</span>
                              </>
                            ) : (
                              <>
                                <Copy size={12} />
                                <span>Copiar</span>
                              </>
                            )}
                          </button>
                          
                          {/* Menú desplegable */}
                          {copyMenuOrderId === order.id && (
                            <div className="absolute right-0 top-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-50 min-w-[160px]">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  copyOrderToClipboard(order);
                                  setCopyMenuOrderId(null);
                                }}
                                className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                              >
                                <Copy size={14} />
                                <span>Copiar Texto</span>
                              </button>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  copyOrderAsImage(order);
                                  setCopyMenuOrderId(null);
                                }}
                                className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                              >
                                <span>🖼️</span>
                                <span>Copiar Imagen</span>
                              </button>
                            </div>
                          )}
                        </div>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleOpenOrderProductsModal(order);
                          }}
                          className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-green-600 hover:text-green-800 hover:bg-green-50 rounded transition-colors"
                          title="Gestionar productos"
                        >
                          <ShoppingBag size={12} />
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteOrder(order.id);
                          }}
                          className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-red-600 hover:text-red-800 hover:bg-red-50 rounded transition-colors"
                          title="Eliminar orden"
                        >
                          <Trash2 size={12} />
                        </button>
                      </div>
              </div>
            </div>
                </div>
              );
            })}
              </div>
              
          {arrayCounters.orders === 0 && (
            <div className="text-center py-8 text-gray-500">
              <ShoppingBag size={32} className="mx-auto mb-2" />
              <p>No hay órdenes en este en vivo</p>
              <p className="text-sm">Crea algunos pedidos para verlos aquí</p>
            </div>
          )}
        </div>
      )}

      {/* Tabla de Productos */}
      {currentEnvivo && arrayCounters.products > 0 && (
        <div className="bg-white rounded-2xl shadow-lg p-6 mb-6">
          <button
            onClick={() => setShowProductsTable(!showProductsTable)}
            className="w-full text-xl font-semibold text-gray-800 mb-4 flex items-center justify-between hover:bg-gray-50 p-2 rounded-lg transition-colors"
          >
            <div className="flex items-center gap-2">
              <Package className="text-purple-600" size={24} />
              📦 Tabla de Productos
            </div>
            {showProductsTable ? (
              <ChevronUp className="text-gray-600" size={24} />
            ) : (
              <ChevronDown className="text-gray-600" size={24} />
            )}
          </button>
          
          {showProductsTable && (
            <>
            <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-3 px-4 font-medium text-gray-700">Producto</th>
                  <th className="text-right py-3 px-4 font-medium text-gray-700">Precio Base</th>
                  <th className="text-center py-3 px-4 font-medium text-gray-700">Vendido</th>
                  <th className="text-right py-3 px-4 font-medium text-gray-700">Ingresos</th>
                  <th className="text-center py-3 px-4 font-medium text-gray-700">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {(products || []).map((product) => {
                  // Calcular ventas para este producto
                  let totalSold = 0;
                  let totalRevenue = 0;
                  
                  (orders || []).forEach(order => {
                    ((order?.items || []) || []).forEach(item => {
                      if (item.productName === product.name) {
                        totalSold += item.quantity;
                        totalRevenue += item.quantity * parseFloat(item.price);
                      }
                    });
                  });

                  // Verificar si el producto está siendo usado en pedidos
                  // const isProductInUse = orders.some(order => 
                  //   order.items && order.items.some(item => 
                  //     item.productName === product.name
                  //   )
                  // );
              
              return (
                    <tr key={product.id} className="border-b border-gray-100 hover:bg-gray-50 transition-colors">
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center">
                            <Package size={16} className="text-purple-600" />
                      </div>
                          <div>
                            <div className="font-medium text-gray-900">{product.name}</div>
                            {product.description && (
                              <div className="text-sm text-gray-500">{product.description}</div>
                            )}
                          </div>
                        </div>
                      </td>
        <td className="py-3 px-4 text-right">
          <span className="font-medium text-gray-900">
            {product.base_price || product.basePrice || product.price ? 
              `$${parseFloat(product.base_price || product.basePrice || product.price).toLocaleString('es-CO')}` : 
              '-'
            }
          </span>
        </td>
                      <td className="py-3 px-4 text-center">
                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-sm font-medium ${
                          totalSold > 0 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-gray-100 text-gray-600'
                        }`}>
                          {totalSold}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-right">
                        <span className="font-medium text-green-600">
                          ${totalRevenue.toLocaleString('es-CO')}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-center">
                        <div className="flex items-center justify-center gap-2">
                      <button
                            onClick={() => handleEditProduct(product)}
                            className="p-1 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded transition-colors"
                            title="Editar producto"
                          >
                            <Edit2 size={16} />
                          </button>
                          <button
                            onClick={() => handleDeleteProduct(product.id)}
                            className="p-1 text-red-600 hover:text-red-800 hover:bg-red-50 rounded transition-colors"
                            title="Eliminar producto"
                          >
                            <Trash2 size={16} />
                      </button>
                    </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            </div>
                  
            {/* Resumen de productos */}
            <div className="mt-4 p-3 bg-purple-50 rounded-lg">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 text-sm">
              <div className="text-center">
                <span className="text-purple-600 font-medium">Total Productos:</span><br />
                <span className="font-semibold text-purple-800">{arrayCounters.products}</span>
              </div>
              <div className="text-center">
                <span className="text-purple-600 font-medium">Productos Vendidos:</span><br />
                <span className="font-semibold text-green-600">
                  {derivedCounters.soldProducts}
                          </span>
              </div>
              <div className="text-center">
                <span className="text-purple-600 font-medium">Total Vendido:</span><br />
                <span className="font-semibold text-blue-600">
                  {products.reduce((total, product) => {
                    let productSold = 0;
                    (orders || []).forEach(order => {
                      (order.items || []).forEach(item => {
                        if (item.productName === product.name) {
                          productSold += item.quantity;
                        }
                      });
                    });
                    return total + productSold;
                  }, 0)}
                          </span>
                        </div>
              <div className="text-center">
                <span className="text-purple-600 font-medium">Ingresos Productos:</span><br />
                <span className="font-semibold text-green-600">
                  ${products.reduce((total, product) => {
                    let productRevenue = 0;
                    (orders || []).forEach(order => {
                      (order.items || []).forEach(item => {
                        if (item.productName === product.name) {
                          productRevenue += item.quantity * parseFloat(item.price);
                        }
                      });
                    });
                    return total + productRevenue;
                  }, 0).toLocaleString('es-CO')}
                </span>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      )}

      {/* Tabla de Clientes */}
      {currentEnvivo && arrayCounters.clients > 0 && (
        <div className="bg-white rounded-2xl shadow-lg p-6 mb-6">
          <button 
            onClick={() => setShowClientsTable(!showClientsTable)}
            className="w-full text-xl font-semibold text-gray-800 mb-4 flex items-center justify-between gap-2 hover:bg-gray-50 p-2 rounded-lg transition-colors"
          >
            <div className="flex items-center gap-2">
              <Users className="text-blue-600" size={24} />
              👥 Tabla de Clientes
            </div>
            {showClientsTable ? <ChevronUp size={24} /> : <ChevronDown size={24} />}
          </button>
          
          {showClientsTable && (
            <div>
              <div className="overflow-x-auto">
                <table className="w-full">
                <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-3 px-4 font-medium text-gray-700">Cliente</th>
                  <th className="text-center py-3 px-4 font-medium text-gray-700">Pedidos</th>
                  <th className="text-right py-3 px-4 font-medium text-gray-700">Total Gastado</th>
                  <th className="text-center py-3 px-4 font-medium text-gray-700">Estado</th>
                  <th className="text-center py-3 px-4 font-medium text-gray-700">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                {(clients && Array.isArray(clients) && clients.length > 0 ? clients : []).map((client) => {
                  // Calcular estadísticas del cliente
                  const clientOrders = (orders || []).filter(order => order.client_username === client.username);
                  const totalSpent = (clientOrders || []).reduce((sum, order) => sum + parseFloat(order.total_amount || 0), 0);
                  const totalOrders = (clientOrders || []).length;
                  const isActive = (clientOrders || []).length > 0;

                    return (
                    <tr key={client.id} className="border-b border-gray-100 hover:bg-gray-50 transition-colors">
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center text-white text-sm font-bold">
                            {client.username.charAt(0).toUpperCase()}
                          </div>
                          <div>
                            <div className="font-medium text-gray-900">@{client.username}</div>
                            {client.name && (
                              <div className="text-sm text-gray-500">{client.name}</div>
                            )}
                          </div>
                        </div>
                        </td>
                      <td className="py-3 px-4 text-center">
                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-sm font-medium ${
                          totalOrders > 0 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-gray-100 text-gray-600'
                        }`}>
                          {totalOrders}
                          </span>
                        </td>
                      <td className="py-3 px-4 text-right">
                        <span className="font-medium text-green-600">
                          ${totalSpent.toLocaleString('es-CO')}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-center">
                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                          isActive 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-gray-100 text-gray-600'
                        }`}>
                          {isActive ? 'ACTIVO' : 'SIN PEDIDOS'}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-center">
                        <div className="flex items-center justify-center gap-2">
                          <button
                            onClick={() => handleEditClient(client)}
                            className="p-1 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded transition-colors"
                            title="Editar cliente"
                          >
                            <Edit2 size={16} />
                          </button>
                          <button
                            onClick={() => handleDeleteClient(client.id)}
                            className="p-1 text-red-600 hover:text-red-800 hover:bg-red-50 rounded transition-colors"
                            title="Eliminar cliente"
                          >
                            <Trash2 size={16} />
                          </button>
                          <button
                            onClick={() => {
                              const clientOrders = (orders || []).filter(o => o.client_username === client.username);
                              if ((clientOrders || []).length > 0) {
                                handleOpenOrderProductsModal(clientOrders[0]);
                              }
                            }}
                            className="p-1 text-green-600 hover:text-green-800 hover:bg-green-50 rounded transition-colors"
                            title="Ver historial"
                          >
                            <ShoppingBag size={16} />
                          </button>
                        </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
                </div>

                {/* Resumen de clientes */}
                <div className="mt-4 p-3 bg-blue-50 rounded-lg">
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4 text-sm">
                    <div className="text-center">
                      <span className="text-blue-600 font-medium">Total Clientes: </span>
                      <span className="font-semibold text-blue-800">{arrayCounters.clients}</span>
                  </div>
                    <div className="text-center">
                      <span className="text-blue-600 font-medium">Clientes Activos: </span>
                      <span className="font-semibold text-green-600">
                        {derivedCounters.activeClients}
                      </span>
                    </div>
                    <div className="text-center">
                      <span className="text-blue-600 font-medium">Sin Pedidos: </span>
                      <span className="font-semibold text-gray-600">
                        {arrayCounters.clients - derivedCounters.activeClients}
                      </span>
                          </div>
                    <div className="text-center">
                      <span className="text-blue-600 font-medium">Tasa de Conversión: </span>
                      <span className="font-semibold text-purple-600">
                        {arrayCounters.clients > 0 ? Math.round((derivedCounters.activeClients / arrayCounters.clients) * 100) : 0}%
                                  </span>
                              </div>
                            </div>
                </div>
            </div>
          )}
        </div>
      )}
                
      {/* Error Message */}
      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mt-4">
          {error}
                </div>
      )}

      {/* Modal de Cierre de En Vivo */}
      {showCloseEnvivoModal && currentEnvivo && (
        <CloseEnvivoModal
          envivoId={currentEnvivo.id}
          envivoName={currentEnvivo.name}
          onClose={() => setShowCloseEnvivoModal(false)}
          onSuccess={handleEnvivoClosed}
        />
      )}

      {/* Modal de Creación de En Vivo */}
      {showEnvivoModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60]">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold mb-4">Crear Nuevo En Vivo</h3>
            
            <form onSubmit={handleEnvivoSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nombre del En Vivo *
                </label>
                <input
                  type="text"
                  value={envivoForm.name}
                  onChange={(e) => setEnvivoForm({...envivoForm, name: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Ej: Live Walmart - Día 1"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Viaje *
                </label>
                <select
                  value={envivoForm.tripId}
                  onChange={(e) => setEnvivoForm({...envivoForm, tripId: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                >
                  <option value="">Seleccionar viaje</option>
                  {(trips || []).map(trip => (
                    <option key={trip.id} value={trip.id}>
                      {trip.name}
                    </option>
                  ))}
                </select>
            </div>

              <div className="flex justify-end gap-3">
              <button
                  type="button"
                onClick={() => {
                    setShowEnvivoModal(false);
                    setEnvivoForm({ name: '', tripId: '' });
                  }}
                  className="px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Cancelar
              </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
                >
                  {loading ? 'Creando...' : 'Crear En Vivo'}
              </button>
            </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal de Agregar Clientes */}
      {showClientModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60]">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <Users className="text-blue-600" size={20} />
              Agregar Clientes
            </h3>
            
          <div className="mb-4 p-3 bg-yellow-50 border border-yellow-300 rounded-lg text-sm text-yellow-800">
            <strong>📋 Reglas de Negocio:</strong><br />
              • Los clientes se agregan automáticamente al en vivo "{currentEnvivo?.name || 'actual'}"<br />
              • Ingresa usuarios de Instagram separados por comas, espacios o saltos de línea
          </div>
          
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Usuarios de Instagram
              </label>
            <textarea
              value={clientsInput}
              onChange={(e) => setClientsInput(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                rows={4}
                placeholder="Ejemplo: maria_garcia, juan_perez, ana_lopez"
              />
              <p className="text-xs text-gray-500 mt-1">
                Separa múltiples usuarios con comas, espacios o saltos de línea
              </p>
            </div>

            <div className="flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowClientModal(false);
                  setClientsInput('');
                }}
                className="px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Cancelar
              </button>
              <button
                onClick={async () => {
                  if (!clientsInput.trim()) {
                    setError('Debes ingresar al menos un usuario');
                    return;
                  }
                  
                  if (!currentEnvivo) {
                    setError('Debes tener un en vivo activo para agregar clientes');
                    return;
                  }

                  setLoading(true);
                  try {
                    // Separar usuarios por comas, espacios o saltos de línea
                    const usernames = clientsInput
                      .split(/[,\s\n]+/)
                      .map(username => username.trim())
                      .filter(username => username.length > 0);

                    debugLog('🚀 Agregando clientes a en vivo:', currentEnvivo.id, usernames);
                    const result = await apiService.client.addToEnvivo(currentEnvivo.id, usernames);
                    debugLog('✅ Clientes agregados:', result);
                    
                    // ✅ Actualizar cache de clientes después de agregar nuevos
                    try {
                      const allClients = await apiService.client.getAll();
                      const clientsArray = Array.isArray(allClients) ? allClients : [];
                      setAllClientsCache(clientsArray);
                      debugLog('✅ Cache de clientes actualizado:', clientsArray.length, 'clientes');
                    } catch (cacheErr) {
                      errorLog('⚠️ Error actualizando cache de clientes (no crítico):', cacheErr);
                    }
                    
                    debugLog('🔄 DEBUG: Llamando a loadClients después de agregar clientes...');
                    await loadClients();
                    debugLog('🔄 DEBUG: loadClients completado');
                  setShowClientModal(false);
                  setClientsInput('');
                    setError('');
                  } catch (err) {
                    setError('Error al agregar clientes: ' + err.message);
                  } finally {
                    setLoading(false);
                  }
                }}
                disabled={loading || !clientsInput.trim()}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
              >
                {loading ? 'Agregando...' : 'Agregar Clientes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Pedido Rápido */}
      {showQuickOrderModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[60]">
          <div className="bg-white rounded-xl p-6 max-w-sm w-full">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-gray-800">⚡ Pedido Rápido</h3>
              <button
                onClick={() => {
                  setShowQuickOrderModal(false);
                  setQuickOrderClient(null);
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X size={20} />
              </button>
            </div>
            
            {/* Selección de Cliente con Autocompletado */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  👤 Seleccionar Cliente
                </label>
              <div className="relative">
                <input
                  type="text"
                  value={quickOrderForm.clientName}
                  onChange={(e) => handleQuickClientInputChange(e.target.value)}
                  onFocus={() => {
                    const trimmedValue = quickOrderForm.clientName.trim();
                    if (trimmedValue.length >= 3 && trimmedValue.length <= 8) {
                      setShowQuickClientSuggestions(true);
                    }
                  }}
                  onBlur={() => setTimeout(() => setShowQuickClientSuggestions(false), 200)}
                  placeholder="Escribe el nombre del cliente..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  autoComplete="off"
                  autoCorrect="off"
                  autoCapitalize="off"
                  spellCheck="false"
                />
                
                {/* Sugerencias de clientes */}
                {showQuickClientSuggestions && quickClientSuggestions.length > 0 && (
                  <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-auto">
                    {quickClientSuggestions.map((client) => (
                      <div
                        key={client.id}
                        onClick={() => selectQuickClient(client)}
                        className="px-3 py-2 hover:bg-purple-50 cursor-pointer border-b border-gray-100"
                      >
                        <div className="font-medium text-gray-800">@{client.username}</div>
                        {client.full_name && (
                          <div className="text-sm text-gray-600">{client.full_name}</div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
              
              {/* Mostrar cliente seleccionado */}
              {quickOrderClient && (
                <div className="mt-2 p-2 bg-green-50 border border-green-200 rounded-lg">
                  <div className="text-sm text-green-700">
                    <span className="font-semibold">Cliente seleccionado:</span> @{quickOrderClient.username}
                    {quickOrderClient.full_name && (
                      <span className="text-gray-600"> - {quickOrderClient.full_name}</span>
                    )}
                </div>
              </div>
            )}
            </div>

            {/* Formulario */}
            <div className="space-y-4">
              {/* Producto con autocompletado */}
              <div className="relative">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  📦 Descripción del Producto
                </label>
                <input
                  id="quick-product-input"
                  type="text"
                  value={quickOrderForm.productName}
                  onChange={(e) => handleQuickProductInputChange(e.target.value)}
                  onKeyDown={handleQuickProductKeyDown}
                  onFocus={() => {
                    const trimmedValue = quickOrderForm.productName.trim();
                    if (trimmedValue.length >= 3 && trimmedValue.length <= 8) {
                      setShowQuickProductSuggestions(true);
                    }
                  }}
                  onBlur={() => setTimeout(() => setShowQuickProductSuggestions(false), 200)}
                  placeholder="Escribe el producto..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  autoComplete="off"
                  autoCorrect="off"
                  autoCapitalize="off"
                  spellCheck="false"
                />
                
                {/* Sugerencias de productos */}
                {showQuickProductSuggestions && quickProductSuggestions.length > 0 && (
                  <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-auto">
                    {quickProductSuggestions.map((product, index) => (
                      <div
                        key={product.id}
                        onClick={() => selectQuickProductSuggestion(product)}
                        className={`px-3 py-2 hover:bg-purple-50 cursor-pointer border-b border-gray-100 ${
                          index === selectedQuickSuggestionIndex ? 'bg-purple-100' : ''
                        }`}
                      >
                        <div className="font-medium text-gray-800">{product.name}</div>
                        <div className="text-sm text-green-600">Precio: ${parseFloat(product.base_price || product.basePrice || product.price || 0).toLocaleString('es-CO')}</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Precio */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  💰 Precio (COP)
                </label>
                <input
                  type="number"
                  value={quickOrderForm.price}
                  onChange={(e) => setQuickOrderForm(prev => ({...prev, price: e.target.value}))}
                  placeholder="0"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
              </div>
            </div>

            {/* Sección de Reconocimiento de Voz */}
            <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-center justify-between mb-2">
                <h4 className="text-sm font-medium text-blue-800">🎤 Crear Pedido por Voz</h4>
                <button
                  onClick={toggleOrderVoiceRecognition}
                  className={`flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium transition-colors ${
                    isListening 
                      ? 'bg-red-100 text-red-700 hover:bg-red-200' 
                      : 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                  }`}
                  title={isListening ? 'Detener grabación manualmente' : 'Iniciar grabación (se detiene automáticamente)'}
                >
                  {isListening ? <MicOff size={14} /> : <Mic size={14} />}
                  {isListening ? 'Detener' : 'Grabar'}
                </button>
              </div>
              
              {/* Indicador de reconocimiento de voz */}
              {isListening && (
                <div className="mb-2 p-2 bg-red-50 border border-red-200 rounded">
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse"></div>
                    <span className="text-xs text-red-700">
                      Escuchando... Di: "cliente/usuario [nombre] producto [nombre] precio [cantidad]" (se detiene automáticamente al terminar)
                    </span>
                  </div>
                </div>
              )}
              
              {/* Mostrar texto reconocido */}
              {voiceText && (
                <div className="mb-2 p-2 bg-green-50 border border-green-200 rounded">
                  <div className="text-xs text-green-700">
                    <span className="font-medium">Texto reconocido:</span> {voiceText}
                  </div>
                </div>
              )}
            </div>

            {/* Botones */}
            <div className="flex gap-3 mt-6">
              <button
                onClick={async () => {
                  // Verificar si hay cliente (seleccionado o por nombre)
                  if (!quickOrderClient && !quickOrderForm.clientName.trim()) {
                    alert('Por favor selecciona un cliente o escribe el nombre del cliente');
                    return;
                  }
                  
                  if (!quickOrderForm.productName.trim() || !quickOrderForm.price.trim()) {
                    alert('Por favor completa todos los campos');
                    return;
                  }

                  setIsCreatingOrder(true);
                  try {
                    // Determinar el nombre del cliente
                    const clientUsername = quickOrderClient ? quickOrderClient.username : quickOrderForm.clientName.trim();
                    
                    // 🔥 NUEVO: Asegurar que el cliente existe en la sesión (crear automáticamente si no existe)
                    if (!quickOrderClient) {
                      debugLog('🔄 Cliente no encontrado en lista, agregando automáticamente al en vivo:', clientUsername);
                      try {
                        await apiService.client.addToEnvivo(currentEnvivo.id, [clientUsername]);
                        debugLog('✅ Cliente agregado automáticamente al en vivo');
                        
                        // ✅ Actualizar cache de clientes después de agregar nuevo cliente
                        try {
                          const allClients = await apiService.client.getAll();
                          const clientsArray = Array.isArray(allClients) ? allClients : [];
                          setAllClientsCache(clientsArray);
                          debugLog('✅ Cache de clientes actualizado:', clientsArray.length, 'clientes');
                        } catch (cacheErr) {
                          errorLog('⚠️ Error actualizando cache de clientes (no crítico):', cacheErr);
                        }
                        
                        await loadClients(); // Recargar lista de clientes
                      } catch (error) {
                        errorLog('❌ Error agregando cliente al en vivo:', error);
                        alert('Error agregando el cliente al en vivo. Verifica que el nombre sea correcto.');
                        return;
                      }
                    }
                    
                    // Buscar o crear producto
                    let product = products.find(p => p.name.toLowerCase() === quickOrderForm.productName.toLowerCase());
                    if (!product) {
                      productLog('🆕 Producto no existe, creándolo:', quickOrderForm.productName);
                      product = await apiService.product.create(
                        quickOrderForm.productName,
                        parseFloat(quickOrderForm.price), // basePrice
                        `Producto creado automáticamente desde pedido rápido`, // description
                        currentEnvivo.id // envivoId
                      );
                      productLog('✅ Producto creado:', product);
                      await loadProducts(); // Recargar productos
                    } else {
                      productLog('✅ Producto ya existe:', product.name);
                    }

                    // ✅ NUEVO: Agregar producto a orden (un producto por request)
                    const productData = {
                      envivoId: currentEnvivo.id,
                      clientUsername: clientUsername,
                      productName: quickOrderForm.productName,
                      price: parseFloat(quickOrderForm.price),
                      quantity: 1
                    };

                    await apiService.order.addProduct(productData);
                    
                    // Recargar datos (productos Y órdenes)
                    await Promise.all([
                      loadProducts(),  // 🔥 NUEVO: Recargar productos
                      loadOrders(),
                      loadKPIs()
                    ]);
                    
                    // Cerrar modal
                    setShowQuickOrderModal(false);
                    setQuickOrderClient(null);
                    setQuickOrderForm({ productName: '', price: '', clientName: '' });
                    
                    orderLog('✅ Pedido rápido creado exitosamente');
                  } catch (error) {
                    errorLog('❌ Error creando pedido rápido:', error);
                    alert('Error creando el pedido');
                  } finally {
                    setIsCreatingOrder(false);
                  }
                }}
                disabled={(!quickOrderClient && !quickOrderForm.clientName.trim()) || !quickOrderForm.productName.trim() || !quickOrderForm.price.trim() || isCreatingOrder}
                className="flex-1 bg-purple-600 hover:bg-purple-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white py-2 rounded-lg font-semibold transition-colors"
              >
                {isCreatingOrder ? '⏳ Creando...' : '✅ Crear Pedido'}
              </button>
              <button
                onClick={() => setShowQuickOrderModal(false)}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Pedido Manual */}
      {showOrderModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60]">
          <div className="bg-white rounded-lg p-6 w-full max-w-lg">
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <Package className="text-purple-600" size={20} />
              Pedido Manual
            </h3>
            
            <div className="mb-4 p-3 bg-purple-50 border border-purple-300 rounded-lg text-sm text-purple-800">
              <strong>📦 Pedido Manual:</strong><br />
              • Agrega múltiples productos de la lista existente<br />
              • Puedes modificar cantidades y precios<br />
              • Se agrega a la orden del cliente seleccionado
            </div>

            <form onSubmit={async (e) => {
              e.preventDefault();
              
              if (!orderForm.clientId) {
                setError('Debes seleccionar un cliente');
                return;
              }
              
              if (!currentEnvivo) {
                setError('Debes tener un en vivo activo');
                return;
              }

              setLoading(true);
              try {
                const client = clients.find(c => c.id === parseInt(orderForm.clientId));
                if (!client) {
                  setError('Cliente no encontrado');
                  return;
                }

                // Agregar cada producto seleccionado
                for (const productSelection of orderForm.products) {
                  if (productSelection.selected && productSelection.quantity > 0) {
                    const productData = {
                      envivoId: currentEnvivo.id,
                      clientUsername: client.username,
                      productName: productSelection.name,
                      price: parseFloat(productSelection.price),
                      quantity: productSelection.quantity
                    };
                    
                    orderLog('🚀 Agregando producto a orden:', productData);
                    await apiService.order.addProduct(productData);
                  }
                }

                orderLog('✅ Pedido manual completado');
                
                // Recargar datos
                await Promise.all([loadProducts(), loadOrders(), loadClients()]);
                
                // Limpiar formulario y cerrar modal
                setOrderForm({
                  clientId: '',
                  products: (products || []).map(p => ({
                    id: p.id,
                    name: p.name,
                    price: p.basePrice || p.price,
                    quantity: 1,
                    selected: false
                  }))
                });
                setShowOrderModal(false);
                setError('');
                
              } catch (err) {
                setError('Error al crear pedido: ' + err.message);
              } finally {
                setLoading(false);
              }
            }} className="space-y-4">
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Cliente *
                </label>
                <select
                  value={orderForm.clientId}
                  onChange={(e) => setOrderForm({...orderForm, clientId: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                  required
                >
                  <option value="">Seleccionar cliente</option>
                  {(clients || [])
                    .sort((a, b) => a.username.toLowerCase().localeCompare(b.username.toLowerCase()))
                    .map(client => (
                    <option key={client.id} value={client.id}>
                      @{client.username} {client.name && `(${client.name})`}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Productos Disponibles
                </label>
                <div className="max-h-60 overflow-y-auto border border-gray-300 rounded-md p-3 space-y-3">
                  {(orderForm.products || [])
                    .sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()))
                    .map((product) => (
                    <div key={product.id} className="flex items-center gap-3 p-2 border border-gray-200 rounded-lg">
                      <input
                        type="checkbox"
                        checked={product.selected}
                        onChange={(e) => {
                          const updatedProducts = (orderForm.products || []).map(p => 
                            p.id === product.id 
                              ? { ...p, selected: e.target.checked }
                              : p
                          );
                          setOrderForm({...orderForm, products: updatedProducts});
                        }}
                        className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                      />
                      
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-gray-900 truncate">
                          {product.name}
                        </div>
                        <div className="text-sm text-gray-500">
                          ${parseFloat(product.price).toLocaleString('es-CO')}
                        </div>
                      </div>
                      
                      {product.selected && (
                        <div className="flex items-center gap-2">
                          <label className="text-xs text-gray-600">Cantidad:</label>
                          <input
                            type="number"
                            min="1"
                            max="99"
                            value={product.quantity}
                            onChange={(e) => {
                              const updatedProducts = (orderForm.products || []).map(p => 
                                p.id === product.id 
                                  ? { ...p, quantity: parseInt(e.target.value) || 1 }
                                  : p
                              );
                              setOrderForm({...orderForm, products: updatedProducts});
                            }}
                            className="w-16 px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-purple-500"
                          />
                        </div>
                      )}
                    </div>
                  ))}
                  
                  {(products || []).length === 0 && (
                    <div className="text-center py-4 text-gray-500 text-sm">
                      No hay productos disponibles. Crea algunos productos primero.
                    </div>
                  )}
                </div>
              </div>

              <div className="flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowOrderModal(false);
                    setOrderForm({
                      clientId: '',
                      products: (products || []).map(p => ({
                        id: p.id,
                        name: p.name,
                        price: p.basePrice || p.price,
                        quantity: 1,
                        selected: false
                      }))
                    });
                  }}
                  className="px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={loading || !orderForm.products.some(p => p.selected)}
                  className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 disabled:opacity-50"
                >
                  {loading ? 'Creando...' : 'Crear Pedido'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal de Productos Rápidos */}
      {showQuickProductModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60]">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <Package className="text-purple-600" size={20} />
              📦 Producto Rápido
            </h3>
            
            <div className="mb-4 p-3 bg-purple-50 border border-purple-300 rounded-lg text-sm text-purple-800">
              <strong>🚀 Creación Rápida de Productos:</strong><br />
              • Crea múltiples productos sin asociarlos a clientes<br />
              • Los productos estarán disponibles para usar en pedidos<br />
              • Puedes agregar productos uno por uno antes de guardar todos
            </div>

            {/* Formulario para agregar producto */}
            <div className="mb-6 p-4 border border-gray-200 rounded-lg">
              <div className="flex items-center justify-between mb-3">
                <h4 className="font-medium text-gray-800">Agregar Nuevo Producto</h4>
                <button
                  onClick={toggleVoiceRecognition}
                  className={`flex items-center gap-0.5 px-1 md:px-1.5 lg:px-2 py-1.5 rounded-lg font-medium transition-colors ${
                    isListening 
                      ? 'bg-red-100 text-red-700 hover:bg-red-200' 
                      : 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                  }`}
                  title={isListening ? 'Detener grabación' : 'Agregar por voz'}
                >
                  {isListening ? <MicOff size={16} /> : <Mic size={16} />}
                  {isListening ? 'Detener' : 'Voz'}
                </button>
              </div>
              
              {/* Indicador de reconocimiento de voz */}
              {isListening && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse"></div>
                    <span className="text-sm text-red-700 font-medium">Escuchando... Di el nombre del producto y el precio</span>
                  </div>
                  <div className="mt-2 text-xs text-red-600">
                    Ejemplo: "Camiseta Nike talla M precio 45000 pesos"<br />
                    Habla con pausas naturales. Haz clic en "Detener" cuando termines
                  </div>
                </div>
              )}
              
              {/* Mostrar texto reconocido */}
              {voiceText && (
                <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg">
                  <div className="text-sm text-green-700">
                    <span className="font-medium">Texto reconocido:</span> {voiceText}
                  </div>
                </div>
              )}
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Nombre del Producto *
                  </label>
                  <input
                    type="text"
                    value={newProduct.name}
                    onChange={(e) => setNewProduct({...newProduct, name: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                    placeholder="Ej: Camiseta Nike talla M"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Precio *
                  </label>
                  <input
                    type="number"
                    value={newProduct.price}
                    onChange={(e) => setNewProduct({...newProduct, price: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                    placeholder="45000"
                    min="0"
                    step="0.01"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Descripción
                  </label>
                  <input
                    type="text"
                    value={newProduct.description}
                    onChange={(e) => setNewProduct({...newProduct, description: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                    placeholder="Opcional"
                  />
                </div>
              </div>
              
              <button
                onClick={addQuickProduct}
                className="w-full bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-md font-medium transition-colors"
              >
                ➕ Agregar Producto
              </button>
            </div>

            {/* Lista de productos agregados */}
            {(quickProducts || []).length > 0 && (
              <div className="mb-6">
                <h4 className="font-medium text-gray-800 mb-3">
                  Productos a Crear ({(quickProducts || []).length})
                </h4>
                
                <div className="space-y-2 max-h-60 overflow-y-auto">
                  {(quickProducts || []).map((product) => (
                    <div key={product.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div className="flex-1">
                        <div className="font-medium text-gray-900">{product.name}</div>
                        {product.description && (
                          <div className="text-sm text-gray-600">{product.description}</div>
                        )}
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="font-medium text-green-600">
                          ${product.price.toLocaleString('es-CO')}
                        </span>
                        <button
                          onClick={() => removeQuickProduct(product.id)}
                          className="p-1 text-red-600 hover:text-red-800 hover:bg-red-50 rounded transition-colors"
                          title="Eliminar producto"
                        >
                          <X size={16} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Botones de acción */}
            <div className="flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowQuickProductModal(false);
                  setQuickProducts([]);
                  setNewProduct({ name: '', price: '', description: '' });
                  setVoiceText('');
                  setIsListening(false);
                }}
                className="px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Cancelar
              </button>
              <button
                onClick={saveQuickProducts}
                disabled={loading || quickProducts.length === 0}
                className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 disabled:opacity-50"
              >
                {loading ? 'Guardando...' : `Guardar ${quickProducts.length} Productos`}
              </button>
            </div>
          </div>
        </div>
      )}


      {/* Modal de Edición de Cliente */}
      {showEditClientModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60]">
          <div className="bg-white rounded-2xl p-6 w-full max-w-md mx-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-800">
                ✏️ Editar Cliente
              </h3>
              <button
                onClick={() => {
                  setShowEditClientModal(false);
                  setEditingClient(null);
                  setEditClientForm({ username: '', name: '' });
                  setError('');
                }}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X size={24} />
              </button>
            </div>

            {error && (
              <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded-lg">
                {error}
              </div>
            )}

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Username de Instagram *
                </label>
                <input
                  type="text"
                  value={editClientForm.username}
                  onChange={(e) => setEditClientForm({...editClientForm, username: e.target.value})}
                  placeholder="@username"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nombre Completo
                </label>
                <input
                  type="text"
                  value={editClientForm.name}
                  onChange={(e) => setEditClientForm({...editClientForm, name: e.target.value})}
                  placeholder="Nombre completo (opcional)"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                />
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  onClick={() => {
                    setShowEditClientModal(false);
                    setEditingClient(null);
                    setEditClientForm({ username: '', name: '' });
                    setError('');
                  }}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleUpdateClient}
                  disabled={loading || !editClientForm.username.trim()}
                  className="flex-1 px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {loading ? 'Guardando...' : 'Guardar Cambios'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Carga de Pago */}
      {showPaymentModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60]">
          <div className="bg-white rounded-2xl w-full max-w-lg mx-4 max-h-[95vh] flex flex-col">
            {/* Header fijo */}
            <div className="flex items-center justify-between p-6 pb-4 border-b border-gray-200 flex-shrink-0">
              <h3 className="text-lg font-semibold text-gray-800">
                {editingPayment ? '✏️ Editar Pago' : '💳 Cargar Pago'} - @{paymentOrder?.client_username}
              </h3>
              <button
                onClick={() => {
                  setShowPaymentModal(false);
                  setEditingPayment(null);
                  setPaymentOrder(null);
                  setPaymentForm({ bank: '', amount: '' });
                  setPaymentImage(null);
                  setPaymentImagePreview(null);
                  setSendExcessToBag(false);
                  setAdvanceBalance(0);
                  setError('');
                }}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X size={24} />
              </button>
            </div>

            {/* Contenido con scroll */}
            <div className="flex-1 overflow-y-auto px-6 py-4">
              {error && (
                <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded-lg">
                  {error}
                </div>
              )}

              <div className="space-y-4">
              {/* Saldo disponible en bolsa */}
              {paymentOrder?.client_id && selectedTrip?.id && (
                <div className="bg-purple-50 rounded-lg p-3 border border-purple-200">
                  <h4 className="font-medium text-purple-800 mb-2 flex items-center gap-2">
                    💼 Saldo Disponible en Bolsa
                  </h4>
                  {loadingAdvanceBalance ? (
                    <div className="text-sm text-purple-600">Cargando...</div>
                  ) : (
                    <div className="text-sm">
                      {advanceBalance > 0 ? (
                        <div>
                          <p className="text-purple-700 font-semibold">
                            ${parseFloat(advanceBalance).toLocaleString('es-CO')} disponible
                          </p>
                          <p className="text-xs text-purple-600 mt-1">
                            Este saldo se aplicará automáticamente al crear/actualizar órdenes para cubrir el abono mínimo.
                          </p>
                        </div>
                      ) : (
                        <p className="text-purple-600">No hay saldo disponible en bolsa para este viaje.</p>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Información de la orden */}
              <div className="bg-gray-50 rounded-lg p-3">
                <h4 className="font-medium text-gray-800 mb-2">Detalles de la Orden</h4>
                <div className="text-sm text-gray-600">
                  <p><strong>Total:</strong> ${parseFloat(paymentOrder?.total_amount || 0).toLocaleString('es-CO')}</p>
                  <p><strong>Productos:</strong> {paymentOrder?.items?.length || 0}</p>
                  {paymentOrder?.min_payment && (
                    <p><strong>Abono Mínimo:</strong> ${parseFloat(paymentOrder.min_payment).toLocaleString('es-CO')}</p>
                  )}
                </div>
              </div>

              {/* Lista de pagos realizados */}
              {paymentOrder && orderPayments[paymentOrder.id] && orderPayments[paymentOrder.id].length > 0 && (
                <div className="bg-blue-50 rounded-lg p-3 border border-blue-200">
                  <h4 className="font-medium text-blue-800 mb-2">Pagos Realizados</h4>
                  <div className="space-y-2 max-h-40 overflow-y-auto">
                    {orderPayments[paymentOrder.id].map((payment) => (
                      <div key={payment.id} className="flex items-center justify-between bg-white rounded-lg p-2 border border-blue-100">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 text-sm font-medium text-gray-800">
                            <span>{payment.bank_name || payment.payment_type || 'Sin banco'}</span>
                            <span className="text-gray-600">-</span>
                            <span className="text-green-600">${parseFloat(payment.amount || 0).toLocaleString('es-CO')}</span>
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <button
                            onClick={() => {
                              setEditingPayment(payment);
                              setPaymentForm({ bank: payment.bank_name || payment.payment_type, amount: payment.amount });
                              setShowPaymentModal(false); // Cerrar modal para reabrir con datos
                              // Reabrir modal con los datos pre-cargados
                              setTimeout(() => {
                                setShowPaymentModal(true);
                              }, 100);
                            }}
                            className="p-1 text-blue-600 hover:bg-blue-100 rounded transition-colors"
                            title="Editar pago"
                          >
                            <Edit2 size={14} />
                          </button>
                          <button
                            onClick={async () => {
                              if (window.confirm('¿Estás seguro de que quieres eliminar este pago?')) {
                                try {
                                  await apiService.payment.delete(payment.id);
                                  // Recargar pagos
                                  const payments = await apiService.payment.getByOrder(paymentOrder.id);
                                  setOrderPayments(prev => ({
                                    ...prev,
                                    [paymentOrder.id]: payments || []
                                  }));
                                  // Recargar órdenes
                                  await loadOrders();
                                } catch (err) {
                                  errorLog('Error eliminando pago:', err);
                                  setError('Error al eliminar pago: ' + err.message);
                                }
                              }
                            }}
                            className="p-1 text-red-600 hover:bg-red-100 rounded transition-colors"
                            title="Eliminar pago"
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Campo de banco con autocompletado */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Banco *
                </label>
                <input
                  type="text"
                  value={paymentForm.bank}
                  onChange={(e) => setPaymentForm({...paymentForm, bank: e.target.value})}
                  placeholder="Escribe el nombre del banco..."
                  list="banks-list"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                  required
                />
                <datalist id="banks-list">
                  {(banks || []).map((bank, index) => (
                    <option key={index} value={bank} />
                  ))}
                </datalist>
              </div>

              {/* Campo de valor */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Valor del Pago *
                </label>
                <input
                  type="number"
                  value={paymentForm.amount}
                  onChange={(e) => setPaymentForm({...paymentForm, amount: e.target.value})}
                  placeholder={`Total de la orden: $${parseFloat(paymentOrder?.total_amount || 0).toLocaleString('es-CO')}`}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                  required
                />
                {/* Mostrar información de excedente si aplica */}
                {paymentForm.amount && paymentOrder && (() => {
                  const excessInfo = calculatePaymentExcess(parseFloat(paymentForm.amount), paymentOrder);
                  if (excessInfo.canSendToBag) {
                    return (
                      <div className="mt-2 p-2 bg-blue-50 border border-blue-200 rounded text-xs text-blue-700">
                        <p>💰 Se aplicará ${excessInfo.neededForMin.toLocaleString('es-CO')} a la orden</p>
                        <p>💼 Excedente: ${excessInfo.excess.toLocaleString('es-CO')}</p>
                      </div>
                    );
                  }
                  return null;
                })()}
              </div>

              {/* Checkbox para enviar excedente a bolsa */}
              {paymentForm.amount && paymentOrder && (() => {
                const excessInfo = calculatePaymentExcess(parseFloat(paymentForm.amount), paymentOrder);
                if (excessInfo.canSendToBag) {
                  return (
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                      <label className="flex items-start gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={sendExcessToBag}
                          onChange={(e) => setSendExcessToBag(e.target.checked)}
                          className="mt-1"
                        />
                        <div className="flex-1">
                          <span className="text-sm font-medium text-yellow-800">
                            Enviar excedente a bolsa de abonos
                          </span>
                          <p className="text-xs text-yellow-700 mt-1">
                            Si marca esta opción, se aplicará el mínimo necesario a la orden
                            (${excessInfo.neededForMin.toLocaleString('es-CO')}) y el resto
                            (${excessInfo.excess.toLocaleString('es-CO')}) irá a la bolsa de abonos.
                          </p>
                          <p className="text-xs text-yellow-600 mt-1 italic">
                            Si no marca esta opción, el pago se aplicará completo a la orden y si sobra dinero después de cubrir el total, el excedente irá automáticamente a la bolsa.
                          </p>
                        </div>
                      </label>
                    </div>
                  );
                }
                return null;
              })()}

              {/* Área de drag & drop para imagen */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Comprobante de Pago (Opcional)
                </label>
                <div
                  className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
                    isDragOver 
                      ? 'border-purple-500 bg-purple-50' 
                      : 'border-gray-300 hover:border-gray-400'
                  }`}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                >
                  {paymentImagePreview ? (
                    <div className="space-y-3">
                      <img 
                        src={paymentImagePreview} 
                        alt="Preview" 
                        className="max-w-full max-h-32 mx-auto rounded-lg"
                      />
                      <p className="text-sm text-green-600">Imagen cargada correctamente</p>
                      <button
                        onClick={() => {
                          setPaymentImage(null);
                          setPaymentImagePreview(null);
                        }}
                        className="text-xs text-red-600 hover:text-red-800"
                      >
                        Eliminar imagen
                      </button>
                    </div>
                  ) : (
                    <div>
                      <p className="text-gray-500 mb-2">
                        Arrastra una imagen aquí o haz clic para seleccionar
                      </p>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleImageUpload}
                        className="hidden"
                        id="payment-image-input"
                      />
                      <label
                        htmlFor="payment-image-input"
                        className="cursor-pointer inline-flex items-center px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                      >
                        Seleccionar Archivo
                      </label>
                    </div>
                  )}
                </div>
              </div>

              </div>
            </div>

            {/* Footer fijo con botones */}
            <div className="flex gap-3 p-6 pt-4 border-t border-gray-200 flex-shrink-0">
              <button
                onClick={() => {
                  setShowPaymentModal(false);
                  setPaymentOrder(null);
                  setPaymentForm({ bank: '', amount: '' });
                  setPaymentImage(null);
                  setPaymentImagePreview(null);
                  setSendExcessToBag(false);
                  setAdvanceBalance(0);
                  setError('');
                }}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={handleSubmitPayment}
                disabled={loading || !paymentForm.bank.trim() || !paymentForm.amount.trim()}
                className="flex-1 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {loading ? 'Guardando...' : 'Guardar Pago'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Edición de Pago */}
      {showEditPaymentModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60]">
          <div className="bg-white rounded-2xl p-6 w-full max-w-lg mx-4 max-h-[95vh]">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-800">
                ✏️ Editar Pago - @{paymentOrder?.client_username}
              </h3>
              <button
                onClick={() => {
                  setShowEditPaymentModal(false);
                  setEditingPayment(null);
                  setPaymentForm({ bank: '', amount: '' });
                  setError('');
                  setShowPaymentModal(true); // Volver al modal principal
                }}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X size={24} />
              </button>
            </div>

            {error && (
              <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded-lg">
                {error}
              </div>
            )}

            <div className="space-y-4">
              {/* Campo de banco */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Banco *
                </label>
                <input
                  type="text"
                  value={paymentForm.bank}
                  onChange={(e) => setPaymentForm({...paymentForm, bank: e.target.value})}
                  placeholder="Escribe el nombre del banco..."
                  list="banks-list"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                  required
                />
                <datalist id="banks-list">
                  {(banks || []).map((bank, index) => (
                    <option key={index} value={bank} />
                  ))}
                </datalist>
              </div>

              {/* Campo de valor */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Valor del Pago *
                </label>
                <input
                  type="number"
                  value={paymentForm.amount}
                  onChange={(e) => setPaymentForm({...paymentForm, amount: e.target.value})}
                  placeholder={`Total de la orden: $${parseFloat(paymentOrder?.total_amount || 0).toLocaleString('es-CO')}`}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                  required
                />
              </div>

              {/* Botones */}
              <div className="flex gap-3 pt-4">
                <button
                  onClick={() => {
                    setShowEditPaymentModal(false);
                    setEditingPayment(null);
                    setPaymentForm({ bank: '', amount: '' });
                    setError('');
                    setShowPaymentModal(true); // Volver al modal principal
                  }}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleUpdatePayment}
                  disabled={loading || !paymentForm.bank.trim() || !paymentForm.amount.trim()}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {loading ? 'Actualizando...' : 'Actualizar Pago'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Edición de Productos */}
      {showEditProductModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-lg">
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <Edit2 size={20} className="text-blue-600" />
              Editar Producto
            </h3>
            
            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
                <p className="text-red-600 text-sm">{error}</p>
              </div>
            )}

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nombre del Producto *
                </label>
                <input
                  type="text"
                  value={editProductForm.name}
                  onChange={(e) => setEditProductForm({...editProductForm, name: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Nombre del producto"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Precio Base (COP) *
                </label>
                <input
                  type="number"
                  value={editProductForm.basePrice}
                  onChange={(e) => setEditProductForm({...editProductForm, basePrice: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="0"
                  min="0"
                  step="100"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Descripción
                </label>
                <textarea
                  value={editProductForm.description}
                  onChange={(e) => setEditProductForm({...editProductForm, description: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Descripción del producto (opcional)"
                  rows={3}
                />
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={handleUpdateProduct}
                disabled={loading || !editProductForm.name.trim() || !editProductForm.basePrice}
                className="flex-1 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white py-2 rounded-lg font-semibold transition-colors"
              >
                {loading ? 'Actualizando...' : 'Actualizar Producto'}
              </button>
              <button
                onClick={() => {
                  setShowEditProductModal(false);
                  setEditingProduct(null);
                  setEditProductForm({ name: '', basePrice: '', description: '' });
                  setError('');
                }}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Edición de Item de Orden */}
      {showEditOrderItemModal && editingOrderItem && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60]">
          <div className="bg-white rounded-lg p-6 w-full max-w-lg">
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <Edit2 size={20} className="text-blue-600" />
              Editar Producto de la Orden
            </h3>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nombre del Producto
                </label>
                <input
                  type="text"
                  value={editOrderItemForm.productName}
                  onChange={(e) => setEditOrderItemForm({...editOrderItemForm, productName: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Nombre del producto"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Precio
                </label>
                <input
                  type="number"
                  value={editOrderItemForm.price}
                  onChange={(e) => setEditOrderItemForm({...editOrderItemForm, price: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="0"
                  min="0"
                  step="0.01"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Cantidad
                </label>
                <input
                  type="number"
                  value={editOrderItemForm.quantity}
                  onChange={(e) => setEditOrderItemForm({...editOrderItemForm, quantity: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="1"
                  min="1"
                />
              </div>

              {/* Preview del subtotal */}
              <div className="bg-gray-50 p-3 rounded-lg">
                <div className="text-sm text-gray-600">
                  <strong>Subtotal:</strong> ${(() => {
                    const price = parseFloat(editOrderItemForm.price) || 0;
                    const quantity = parseInt(editOrderItemForm.quantity) || 1;
                    return (price * quantity).toLocaleString('es-CO');
                  })()}
                </div>
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={handleSaveOrderItemEdit}
                disabled={loading || !editOrderItemForm.productName.trim() || !editOrderItemForm.price || !editOrderItemForm.quantity}
                className="flex-1 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white py-2 rounded-lg font-semibold transition-colors"
              >
                {loading ? 'Actualizando...' : 'Actualizar Producto'}
              </button>
              <button
                onClick={() => {
                  setShowEditOrderItemModal(false);
                  setEditingOrderItem(null);
                  setEditOrderItemForm({ productName: '', price: '', quantity: '' });
                  setError('');
                }}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Gestión de Productos de Orden */}
      {showOrderProductsModal && selectedOrderForProducts && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
            {/* Header */}
            <div className="bg-gradient-to-r from-green-500 to-green-600 px-6 py-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="bg-white bg-opacity-20 p-2 rounded-lg">
                  <ShoppingBag size={24} className="text-white" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-white">Gestionar Productos</h3>
                  <p className="text-green-100 text-sm">
                    Orden de @{selectedOrderForProducts.client_username || selectedOrderForProducts.clientUsername}
                  </p>
                </div>
              </div>
              <button
                onClick={() => {
                  setShowOrderProductsModal(false);
                  setSelectedOrderForProducts(null);
                }}
                className="p-2 hover:bg-white hover:bg-opacity-20 rounded-lg transition-colors"
              >
                <X size={24} className="text-white" />
              </button>
            </div>

            {/* Resumen de la orden */}
            <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <p className="text-xs text-gray-600">Total Orden</p>
                  <p className="text-lg font-bold text-gray-900">
                    ${parseFloat(selectedOrderForProducts.total_amount || 0).toLocaleString('es-CO')}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-gray-600">Productos</p>
                  <p className="text-lg font-bold text-gray-900">
                    {selectedOrderForProducts.items?.length || 0}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-gray-600">Estado</p>
                  <p className={`text-lg font-bold ${
                    selectedOrderForProducts.status === 'completed' ? 'text-green-600' : 'text-yellow-600'
                  }`}>
                    {selectedOrderForProducts.status === 'pending' ? 'PENDIENTE' : 
                     selectedOrderForProducts.status === 'completed' ? 'COMPLETADO' : 
                     selectedOrderForProducts.status?.toUpperCase()}
                  </p>
                </div>
              </div>
            </div>

            {/* Lista de productos */}
            <div className="flex-1 overflow-y-auto px-6 py-4">
              <div className="space-y-3">
                {((selectedOrderForProducts?.items || []) || []).map((item, index) => {
                  const itemTotal = parseFloat(item.price || 0) * (item.quantity || 1);
                  return (
                    <div key={index} className="bg-gray-50 rounded-lg p-4 border border-gray-200 hover:border-green-300 transition-colors">
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <h4 className="font-semibold text-gray-900">{item.productName || 'Sin nombre'}</h4>
                          <div className="flex items-center gap-4 mt-2 text-sm text-gray-600">
                            <span>Precio: ${parseFloat(item.price || 0).toLocaleString('es-CO')}</span>
                            <span>•</span>
                            <span>Cantidad: {item.quantity || 1}</span>
                            <span>•</span>
                            <span className="font-semibold text-green-600">
                              Subtotal: ${itemTotal.toLocaleString('es-CO')}
                            </span>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 ml-4">
                          <button
                            onClick={() => {
                              setShowOrderProductsModal(false);
                              handleEditItemInOrder(selectedOrderForProducts.id, index, item);
                            }}
                            className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                            title="Editar producto"
                          >
                            <Edit2 size={18} />
                          </button>
                          <button
                            onClick={() => {
                              if (window.confirm(`¿Eliminar "${item.productName}" de la orden?`)) {
                                handleRemoveItemFromOrder(selectedOrderForProducts.id, index);
                                setShowOrderProductsModal(false);
                              }
                            }}
                            className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                            title="Eliminar producto"
                          >
                            <Trash2 size={18} />
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Footer con acciones */}
            <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex items-center justify-between">
              <div className="text-sm text-gray-600">
                Total de {selectedOrderForProducts.items?.length || 0} producto(s)
              </div>
              <button
                onClick={() => {
                  setShowOrderProductsModal(false);
                  setSelectedOrderForProducts(null);
                }}
                className="px-6 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg font-semibold transition-colors"
              >
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Botón "Volver arriba" flotante y buscador */}
      {showScrollTop && (
        <>
          {/* Botón de la derecha (solo escritorio lg) */}
          <button
            onClick={() => {
              window.scrollTo({ top: 0, behavior: 'smooth' });
            }}
            className="hidden lg:flex fixed bottom-8 right-8 bg-blue-600 hover:bg-blue-700 text-white p-4 rounded-full shadow-lg transition-all duration-300 z-50 items-center justify-center hover:scale-110"
            title="Volver arriba"
          >
            <ArrowUp size={24} />
          </button>

          {/* Buscador y botón "Ir Arriba" para iPad y Mobile (md hasta lg) */}
          <div className={`md:flex lg:hidden fixed bottom-4 right-4 z-50 flex items-center gap-3 transition-all duration-300 ${
            showMobileSearch ? 'translate-y-0 opacity-100' : 'translate-y-[200%] opacity-0 pointer-events-none'
          }`}>
            {/* Campo de búsqueda expandible (se expande hacia la izquierda cuando se abre) */}
            <div className={`flex items-center bg-white shadow-lg rounded-full border border-gray-200 overflow-hidden transition-all duration-300 ${
              isSearchExpanded ? 'w-64 opacity-100' : 'w-0 opacity-0 overflow-hidden'
            }`}>
              {/* Botón X para cerrar (a la izquierda del campo) */}
              {isSearchExpanded && (
                <button
                  type="button"
                  onClick={() => {
                    setIsSearchExpanded(false);
                    setMobileSearchTerm('');
                    // Limpiar resaltados
                    const existingHighlights = document.querySelectorAll('.search-highlight');
                    existingHighlights.forEach(el => {
                      const parent = el.parentNode;
                      if (parent && parent.className === 'search-highlight-container') {
                        parent.replaceWith(document.createTextNode(el.textContent));
                      }
                    });
                    // Normalizar el DOM
                    document.body.normalize();
                  }}
                  className="bg-blue-600 hover:bg-blue-700 text-white p-3 rounded-full flex items-center justify-center flex-shrink-0 transition-colors"
                  title="Cerrar búsqueda"
                >
                  <X size={20} className="font-bold" strokeWidth={3} />
                </button>
              )}
              <input
                type="text"
                value={mobileSearchTerm}
                onChange={(event) => {
                  setMobileSearchTerm(event.target.value);
                  // Si el campo se limpia, eliminar resaltados
                  if (!event.target.value.trim()) {
                    const existingHighlights = document.querySelectorAll('.search-highlight');
                    existingHighlights.forEach(el => {
                      const parent = el.parentNode;
                      if (parent && parent.className === 'search-highlight-container') {
                        parent.replaceWith(document.createTextNode(el.textContent));
                      }
                    });
                    document.body.normalize();
                  }
                }}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') {
                    event.preventDefault();
                    handleMobileQuickSearch();
                  }
                  if (event.key === 'Escape') {
                    setIsSearchExpanded(false);
                    setMobileSearchTerm('');
                // Limpiar resaltados
                clearHighlights();
                  }
                }}
                onBlur={() => {
                  // Cerrar si está vacío al perder el foco
                  if (!mobileSearchTerm.trim()) {
                    setIsSearchExpanded(false);
                // Limpiar resaltados
                clearHighlights();
                  }
                }}
                placeholder="Buscar en la página..."
                className={`px-4 py-3 text-sm text-gray-700 outline-none transition-all ${
                  isSearchExpanded ? 'w-full flex-1' : 'w-0 hidden'
                }`}
                aria-label="Buscar en la página"
                autoFocus={isSearchExpanded}
              />
              {isSearchExpanded && (
                <button
                  type="button"
                  onClick={handleMobileQuickSearch}
                  className="px-4 py-3 bg-blue-600 text-white hover:bg-blue-700 transition-colors flex items-center justify-center flex-shrink-0"
                  title="Buscar coincidencia"
                >
                  <Search size={20} />
                </button>
              )}
            </div>

            {/* Botón de lupa (solo visible cuando NO está expandido) */}
            <button
              type="button"
              onClick={() => setIsSearchExpanded(!isSearchExpanded)}
              className={`bg-blue-600 hover:bg-blue-700 text-white p-4 rounded-full shadow-lg transition-all duration-300 flex items-center justify-center hover:scale-110 z-50 ${
                isSearchExpanded ? 'hidden' : 'flex'
              }`}
              title="Buscar en la página"
            >
              <Search size={24} />
            </button>

            {/* Botón "Ir Arriba" */}
            <button
              onClick={() => {
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }}
              className="bg-blue-600 hover:bg-blue-700 text-white p-4 rounded-full shadow-lg transition-all duration-300 flex items-center justify-center hover:scale-110 z-50"
              title="Volver arriba"
            >
              <ArrowUp size={24} />
            </button>
          </div>

          {/* Buscador completo para Mobile (menor a md) */}
          <div className={`md:hidden fixed bottom-4 right-4 left-4 z-50 flex flex-col items-center gap-3 transition-transform duration-300 ease-in-out ${
            showMobileSearch ? 'translate-y-0 opacity-100' : 'translate-y-[200%] opacity-0 pointer-events-none'
          }`}>
            <div className="flex items-center bg-white shadow-lg rounded-full border border-gray-200 overflow-hidden w-full max-w-sm mx-auto">
              <input
                type="text"
                value={mobileSearchTerm}
                onChange={(event) => {
                  setMobileSearchTerm(event.target.value);
                  // Si el campo se limpia, eliminar resaltados
                  if (!event.target.value.trim()) {
                    const existingHighlights = document.querySelectorAll('.search-highlight');
                    existingHighlights.forEach(el => {
                      const parent = el.parentNode;
                      if (parent && parent.className === 'search-highlight-container') {
                        parent.replaceWith(document.createTextNode(el.textContent));
                      }
                    });
                    document.body.normalize();
                  }
                }}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') {
                    event.preventDefault();
                    handleMobileQuickSearch();
                  }
                }}
                placeholder="Buscar en la página..."
                className="flex-1 px-4 py-3 text-sm text-gray-700 outline-none"
                aria-label="Buscar en la página"
              />
              <button
                type="button"
                onClick={handleMobileQuickSearch}
                className="px-4 py-3 bg-blue-600 text-white hover:bg-blue-700 transition-colors flex items-center justify-center"
                title="Buscar coincidencia"
              >
                <Search size={20} />
              </button>
            </div>

            {showScrollTop && (
              <button
                onClick={() => {
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }}
                className="bg-blue-600 hover:bg-blue-700 text-white p-4 rounded-full shadow-lg transition-all duration-300 flex items-center justify-center hover:scale-110"
                title="Volver arriba"
              >
                <ArrowUp size={24} />
              </button>
            )}
          </div>
        </>
      )}

    </div>
    </>
  );
};

export default Dashboard;